import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ESTRATEGIAS_PREDICTOR,
  formatearCLP,
  formatearPorcentajeSimple,
  ROLES,
  ETIQUETAS_ROL,
  type ModoEstrategiaPredictor,
  type Rol,
  type ModalidadMonto,
} from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBasura,
  IconoBuscar,
  IconoCalculadora,
  IconoCerebro,
  IconoCerrar,
  IconoCopiar,
  IconoDerecha,
  IconoDescargar,
  IconoEditar,
  IconoEmpresa,
  IconoEtiqueta,
  IconoHistorial,
  IconoMas,
  IconoMotorIA,
  IconoRestablecer,
  IconoUsuarios,
} from '../componentes/Iconos';
import { Cargando, ErrorCarga, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import { ModalPagarGasto } from './costos/ModalPagarGasto';
import { ModalRegistrarRemuneracion } from './empresa/ModalRegistrarRemuneracion';
import { ModalPagarRemuneracion } from './empresa/ModalPagarRemuneracion';
import { ModalAsignarBonoLicitacion } from './empresa/ModalAsignarBonoLicitacion';
import { ModalDetallePrevired } from './empresa/ModalDetallePrevired';
import { useAuth } from '../contextos/AuthContext';
import { useAvisos } from '../contextos/AvisosContext';
import {
  useAuditoriaConfiguracion,
  useCategoriasComercialesConfig,
  useCategoriasGastoConfig,
  useConfiguracionEmpresa,
  useConsolaCostosServicios,
  useConsolaMotor,
  useConsolaRrhh,
  useGastos,
  useParametrosPredictorConfig,
  usePlanillaPrevired,
  useRemuneraciones,
  useUsuariosEmpresa,
} from '../lib/consultas';
import {
  useActualizarCategoriaComercial,
  useActualizarCategoriaGastoConfig,
  useActualizarConfiguracionEmpresa,
  useActualizarParametrosPredictorConfig,
  useActualizarUsuarioEmpresa,
  useAplicarEstrategiaMotor,
  useCalcularMontosTributarios,
  useCargarNominaMes,
  useCrearCategoriaComercial,
  useCrearCategoriaGastoConfig,
  useCrearGastoEnProyecto,
  useCrearGastoRecurrente,
  useCrearUsuarioEmpresa,
  useEliminarCategoriaComercial,
  useEliminarCategoriaGastoConfig,
  useEliminarGasto,
  useEliminarGastoRecurrente,
  useEliminarUsuarioEmpresa,
  useGastosRecurrentes,
  useGenerarRecurrentes,
  useRestablecerParametrosPredictorConfig,
} from '../lib/mutaciones';

function formatearFechaCorta(fecha: string | Date | null | undefined): string {
  if (!fecha) return '—';
  const f = typeof fecha === 'string' ? new Date(fecha) : fecha;
  return isNaN(f.getTime()) ? '—' : f.toLocaleDateString('es-CL');
}

function formatearFechaHora(fecha: string | Date | null | undefined): string {
  if (!fecha) return '—';
  const f = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(f.getTime())) return '—';
  return `${f.toLocaleDateString('es-CL')} ${f.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

type PestanaConfig =
  | 'perfil'
  | 'usuarios'
  | 'categorias'
  | 'gastos-config'
  | 'auditoria'
  | 'gastos-empresa'
  | 'calculadora'
  | 'predictor';

const PESTANAS_VALIDAS: PestanaConfig[] = [
  'perfil',
  'usuarios',
  'categorias',
  'gastos-config',
  'auditoria',
  'gastos-empresa',
  'calculadora',
  'predictor',
];

const PALETA_COLORES_SUGERIDOS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#10b981',
  '#06b6d4',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#64748b',
];

const ETIQUETAS_ENTIDAD: Record<string, string> = {
  Tenant: 'Empresa & Fiscal',
  ConfiguracionEmpresa: 'Empresa & Fiscal',
  Categoria: 'Línea de Negocio',
  CategoriaGasto: 'Categoría de Costo',
  ParametrosPredictor: 'Motor de Precios IA',
  Usuario: 'Equipo & Acceso',
};

export function Empresa() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || searchParams.get('pestana');
  const pestanaInicial: PestanaConfig =
    tabParam && PESTANAS_VALIDAS.includes(tabParam as PestanaConfig)
      ? (tabParam as PestanaConfig)
      : 'perfil';

  const [pestanaActiva, setPestanaActiva] = useState<PestanaConfig>(pestanaInicial);
  const { usuario } = useAuth();
  const esAdminOOwner = usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER';

  useEffect(() => {
    if (tabParam && PESTANAS_VALIDAS.includes(tabParam as PestanaConfig) && tabParam !== pestanaActiva) {
      setPestanaActiva(tabParam as PestanaConfig);
    }
  }, [tabParam]);

  const seleccionarPestana = (p: PestanaConfig) => {
    setPestanaActiva(p);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', p);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
          Configuración de la Empresa y del Sistema
        </h1>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Centro de control administrativo unificado. Gestiona datos corporativos, equipo, líneas de negocio,
          centros de costo, registro inmutable de auditoría, estructura financiera y calibración de IA.
        </p>
      </div>

      <nav className="config-tabs-nav" aria-label="Secciones de configuración">
        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'perfil' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('perfil')}
        >
          <IconoEmpresa size={15} />
          <span>Perfil & Fiscal</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'usuarios' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('usuarios')}
        >
          <IconoUsuarios size={15} />
          <span>Equipo & Usuarios</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'categorias' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('categorias')}
        >
          <IconoEtiqueta size={15} />
          <span>Líneas de Negocio</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'gastos-config' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('gastos-config')}
        >
          <IconoEtiqueta size={15} />
          <span>Categorías de Costo</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'auditoria' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('auditoria')}
        >
          <IconoHistorial size={15} />
          <span>Auditoría</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'gastos-empresa' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('gastos-empresa')}
        >
          <IconoEmpresa size={15} />
          <span>Gastos & RRHH (OPEX / COGS)</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'calculadora' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('calculadora')}
        >
          <IconoCalculadora size={15} />
          <span>Calculadora IVA</span>
        </button>

        <button
          type="button"
          className={`config-tab-btn${pestanaActiva === 'predictor' ? ' activo' : ''}`}
          onClick={() => seleccionarPestana('predictor')}
        >
          <IconoMotorIA size={15} />
          <span>Calibración IA</span>
        </button>
      </nav>

      {pestanaActiva === 'perfil' && <PestanaPerfilEmpresa esAdminOOwner={esAdminOOwner} />}
      {pestanaActiva === 'usuarios' && <PestanaUsuariosEmpresa esAdminOOwner={esAdminOOwner} />}
      {pestanaActiva === 'categorias' && <PestanaCategoriasComerciales esAdminOOwner={esAdminOOwner} />}
      {pestanaActiva === 'gastos-config' && <PestanaCategoriasGasto esAdminOOwner={esAdminOOwner} />}
      {pestanaActiva === 'auditoria' && <PestanaAuditoriaConfiguracion />}
      {pestanaActiva === 'gastos-empresa' && <PestanaGastosGenerales />}
      {pestanaActiva === 'calculadora' && <PestanaCalculadoraTributaria />}
      {pestanaActiva === 'predictor' && <PestanaCalibracionPredictor esAdminOOwner={esAdminOOwner} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. PERFIL CORPORATIVO & PARÁMETROS TRIBUTARIOS
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaPerfilEmpresa({ esAdminOOwner }: { esAdminOOwner: boolean }) {
  const navegar = useNavigate();
  const avisos = useAvisos();
  const configQuery = useConfiguracionEmpresa();
  const actualizarMut = useActualizarConfiguracionEmpresa();

  const [form, setForm] = useState({
    nombre: '',
    rut: '',
    giro: '',
    ivaPct: 19,
    modalidadMontoDefault: 'BRUTO' as ModalidadMonto,
    condicionPagoDefault: 'DIAS_30',
    moneda: 'CLP',
    direccion: '',
    region: '',
    comuna: '',
    telefono: '',
    emailContacto: '',
    sitioWeb: '',
  });

  useEffect(() => {
    if (configQuery.data) {
      const d = configQuery.data;
      setForm({
        nombre: d.nombre ?? '',
        rut: d.rut ?? '',
        giro: d.giro ?? '',
        ivaPct: d.ivaPct ?? 19,
        modalidadMontoDefault: (d.modalidadMontoDefault as ModalidadMonto) ?? 'BRUTO',
        condicionPagoDefault: d.condicionPagoDefault ?? 'DIAS_30',
        moneda: d.moneda ?? 'CLP',
        direccion: d.direccion ?? '',
        region: d.region ?? '',
        comuna: d.comuna ?? '',
        telefono: d.telefono ?? '',
        emailContacto: d.emailContacto ?? '',
        sitioWeb: d.sitioWeb ?? '',
      });
    }
  }, [configQuery.data]);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!esAdminOOwner) {
      avisos.error('Solo Administradores o Dueños pueden modificar la configuración de la empresa.');
      return;
    }
    try {
      await actualizarMut.mutateAsync({
        nombre: form.nombre.trim(),
        rut: form.rut.trim() || null,
        giro: form.giro.trim() || null,
        ivaPct: Number(form.ivaPct),
        modalidadMontoDefault: form.modalidadMontoDefault,
        condicionPagoDefault: form.condicionPagoDefault,
        moneda: form.moneda.trim() || 'CLP',
        direccion: form.direccion.trim() || null,
        region: form.region.trim() || null,
        comuna: form.comuna.trim() || null,
        telefono: form.telefono.trim() || null,
        emailContacto: form.emailContacto.trim() || null,
        sitioWeb: form.sitioWeb.trim() || null,
      });
      avisos.exito('Configuración corporativa actualizada correctamente.');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo guardar la configuración.');
    }
  };

  if (configQuery.isLoading) return <Cargando texto="Cargando configuración de la empresa…" />;
  if (configQuery.isError) return <ErrorCarga error={configQuery.error} reintentar={() => void configQuery.refetch()} />;

  return (
    <>
      {/* Resumen Superior de Identidad */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Razón Social Oficial"
          valor={form.nombre || 'Lexicore CRM'}
          acento="var(--accent-purple)"
          nota={`RUT: ${form.rut || 'No especificado'}`}
        />
        <TarjetaKpi
          etiqueta="Régimen Tributario"
          valor={`${form.ivaPct}% IVA`}
          acento="var(--accent-yellow)"
          colorValor="var(--accent-yellow)"
          nota={`Modalidad default: ${form.modalidadMontoDefault}`}
        />
        <TarjetaKpi
          etiqueta="Condición de Pago"
          valor={form.condicionPagoDefault.replace('_', ' ')}
          acento="var(--accent-blue)"
          nota={`Moneda contable: ${form.moneda}`}
        />
        <TarjetaKpi
          etiqueta="Sistema Corporativo"
          valor="LEXICORE LABS"
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Sistema Operativo Interno Exclusivo (No-SaaS)"
        />
      </div>

      {!esAdminOOwner && (
        <div
          style={{
            background: 'var(--accent-yellow-soft)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12.5,
            color: 'var(--text-secondary)',
          }}
        >
          <IconoAlerta size={16} />
          <span>
            <strong>Modo solo lectura:</strong> Se requieren permisos de Administrador o Dueño para modificar los datos
            corporativos de la empresa.
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
        <Panel
          titulo="Identidad Corporativa & Régimen Tributario"
          subtitulo="Datos tributarios oficiales utilizados en contratos, órdenes de compra y facturación"
        >
          <form onSubmit={guardar} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label htmlFor="cfg_nombre">Razón Social / Nombre Comercial *</label>
              <input
                id="cfg_nombre"
                type="text"
                value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                disabled={!esAdminOOwner}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="cfg_rut">RUT de la Empresa</label>
                <input
                  id="cfg_rut"
                  type="text"
                  placeholder="Ej: 77.123.456-7"
                  value={form.rut}
                  onChange={(e) => setForm((f) => ({ ...f, rut: e.target.value }))}
                  disabled={!esAdminOOwner}
                />
              </div>
              <div className="form-group">
                <label htmlFor="cfg_plan">Entorno Corporativo</label>
                <input
                  id="cfg_plan"
                  type="text"
                  value="LEXICORE LABS SYSTEM (Propiedad Exclusiva)"
                  readOnly
                  style={{
                    background: 'var(--accent-purple-soft)',
                    color: 'var(--accent-purple)',
                    fontWeight: 700,
                    border: '1px solid rgba(139, 92, 246, 0.35)',
                    cursor: 'default',
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="cfg_giro">Giro Comercial / Actividad Económica</label>
              <input
                id="cfg_giro"
                type="text"
                placeholder="Ej: Servicios de Tecnología, Consultoría e Integración TI"
                value={form.giro}
                onChange={(e) => setForm((f) => ({ ...f, giro: e.target.value }))}
                disabled={!esAdminOOwner}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="cfg_iva">Tasa de IVA (%)</label>
                <input
                  id="cfg_iva"
                  type="number"
                  min={0}
                  max={100}
                  value={form.ivaPct}
                  onChange={(e) => setForm((f) => ({ ...f, ivaPct: Number(e.target.value) }))}
                  disabled={!esAdminOOwner}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="cfg_modalidad">Modalidad por Defecto</label>
                <select
                  id="cfg_modalidad"
                  value={form.modalidadMontoDefault}
                  onChange={(e) => setForm((f) => ({ ...f, modalidadMontoDefault: e.target.value as ModalidadMonto }))}
                  disabled={!esAdminOOwner}
                >
                  <option value="BRUTO">BRUTO (Con IVA incluido)</option>
                  <option value="NETO">NETO (Sin IVA)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="cfg_pago">Condición de Pago</label>
                <select
                  id="cfg_pago"
                  value={form.condicionPagoDefault}
                  onChange={(e) => setForm((f) => ({ ...f, condicionPagoDefault: e.target.value }))}
                  disabled={!esAdminOOwner}
                >
                  <option value="AL_DIA">Al Día / Contado</option>
                  <option value="DIAS_30">30 Días</option>
                  <option value="DIAS_60">60 Días</option>
                  <option value="DIAS_90">90 Días</option>
                </select>
              </div>

              <div
                style={{
                  gridColumn: '1 / -1',
                  background: 'var(--accent-green-soft)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--accent-green)' }}>Gestión de Cobranza Estatal:</strong> Este plazo calibra la fecha de vencimiento y el cálculo del DSO (Días Promedio de Cobro) en el Módulo de Facturación & Cobranza.
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => navegar('/cobranza')}
                  style={{
                    fontSize: 11,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span>Ver Cartera</span>
                  <IconoDerecha size={12} />
                </button>
              </div>

              <div
                style={{
                  gridColumn: '1 / -1',
                  background: 'var(--accent-blue-soft)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--accent-blue)' }}>Cronograma Operativo & Financiero:</strong> Las fechas de vencimiento calculadas se proyectan en tiempo real en el Calendario Multimes para anticipar el flujo de caja.
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => navegar('/calendario')}
                  style={{
                    fontSize: 11,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span>Ver Calendario</span>
                  <IconoDerecha size={12} />
                </button>
              </div>

              <div
                style={{
                  gridColumn: '1 / -1',
                  background: 'var(--accent-purple-soft, rgba(139, 92, 246, 0.12))',
                  border: '1px solid rgba(139, 92, 246, 0.25)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--accent-purple)' }}>Torre de Control & Directorio:</strong> La razón social, RUT y plazos alimentan el Informe Ejecutivo formal y el KPI de Días Promedio de Cobro (DSO) del Dashboard General.
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => navegar('/')}
                  style={{
                    fontSize: 11,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span>Ver Dashboard</span>
                  <IconoDerecha size={12} />
                </button>
              </div>
            </div>

            {esAdminOOwner && (
              <div style={{ marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={actualizarMut.isPending}>
                  {actualizarMut.isPending ? 'Guardando cambios…' : 'Guardar Datos Corporativos'}
                </button>
              </div>
            )}
          </form>
        </Panel>

        <Panel
          titulo="Contacto, Casa Matriz & Canales Oficiales"
          subtitulo="Información de contacto que aparece en propuestas, bitácoras y exportaciones"
        >
          <form onSubmit={guardar} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label htmlFor="cfg_dir">Dirección Casa Matriz</label>
              <input
                id="cfg_dir"
                type="text"
                placeholder="Ej: Av. Providencia 1234, Oficina 601"
                value={form.direccion}
                onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))}
                disabled={!esAdminOOwner}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="cfg_region">Región</label>
                <input
                  id="cfg_region"
                  type="text"
                  placeholder="Ej: Región Metropolitana"
                  value={form.region}
                  onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                  disabled={!esAdminOOwner}
                />
              </div>
              <div className="form-group">
                <label htmlFor="cfg_comuna">Comuna</label>
                <input
                  id="cfg_comuna"
                  type="text"
                  placeholder="Ej: Providencia"
                  value={form.comuna}
                  onChange={(e) => setForm((f) => ({ ...f, comuna: e.target.value }))}
                  disabled={!esAdminOOwner}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="cfg_email">Email Oficial de Contacto</label>
              <input
                id="cfg_email"
                type="email"
                placeholder="contacto@lexicore.cl"
                value={form.emailContacto}
                onChange={(e) => setForm((f) => ({ ...f, emailContacto: e.target.value }))}
                disabled={!esAdminOOwner}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="cfg_tel">Teléfono</label>
                <input
                  id="cfg_tel"
                  type="text"
                  placeholder="+56 9 1234 5678"
                  value={form.telefono}
                  onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
                  disabled={!esAdminOOwner}
                />
              </div>
              <div className="form-group">
                <label htmlFor="cfg_web">Sitio Web</label>
                <input
                  id="cfg_web"
                  type="url"
                  placeholder="https://www.lexicore.cl"
                  value={form.sitioWeb}
                  onChange={(e) => setForm((f) => ({ ...f, sitioWeb: e.target.value }))}
                  disabled={!esAdminOOwner}
                />
              </div>
            </div>

            {form.sitioWeb && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                Enlace público:{' '}
                <a
                  href={form.sitioWeb.startsWith('http') ? form.sitioWeb : `https://${form.sitioWeb}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                >
                  {form.sitioWeb} ↗
                </a>
              </div>
            )}

            {esAdminOOwner && (
              <div style={{ marginTop: 8 }}>
                <button type="submit" className="btn btn-secondary" disabled={actualizarMut.isPending}>
                  {actualizarMut.isPending ? 'Guardando…' : 'Actualizar Canales de Contacto'}
                </button>
              </div>
            )}
          </form>
        </Panel>

        {/* Panel de Cuentas Bancarias Oficiales de Recaudación */}
        <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
          <Panel
            titulo="Cuentas Bancarias Oficiales de Recaudación & Facturación"
            subtitulo="Datos bancarios institucionales validados para recepción de pagos, órdenes de compra de Mercado Público y transferencias de clientes"
            acciones={
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const textoTransferencia = [
                    `DATOS DE TRANSFERENCIA OFICIALES — ${form.nombre || 'LEXICORE SPA'}`,
                    `Banco: Banco de Chile`,
                    `Tipo de Cuenta: Cuenta Corriente (Moneda Nacional CLP)`,
                    `Número de Cuenta: 00-123-45678-90`,
                    `Titular: ${form.nombre || 'Lexicore Technologies SpA'}`,
                    `RUT: ${form.rut || '77.123.456-7'}`,
                    `Email Comprobantes: ${form.emailContacto || 'pagos@lexicore.cl'}`,
                  ].join('\n');
                  navigator.clipboard.writeText(textoTransferencia);
                  avisos.exito('Datos bancarios copiados al portapapeles listos para enviar al cliente o adjuntar en orden de compra.');
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <IconoCopiar size={13} />
                <span>Copiar Datos de Transferencia</span>
              </button>
            }
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              <div
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                    🏦 Cuenta Corriente Principal (Operaciones Chile)
                  </span>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: 'var(--accent-green-soft)',
                      color: 'var(--accent-green)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                    }}
                  >
                    HABILITADA PARA TESORERÍA
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Entidad Bancaria:</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Banco de Chile</span>
                  <span style={{ color: 'var(--text-muted)' }}>Tipo de Cuenta:</span>
                  <span style={{ color: 'var(--text-secondary)' }}>Cuenta Corriente (CLP)</span>
                  <span style={{ color: 'var(--text-muted)' }}>N° de Cuenta:</span>
                  <span className="td-mono" style={{ fontWeight: 700, color: 'var(--accent-purple)' }}>00-123-45678-90</span>
                  <span style={{ color: 'var(--text-muted)' }}>Razón Social:</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{form.nombre || 'Lexicore Technologies SpA'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>RUT Beneficiario:</span>
                  <span className="td-mono" style={{ color: 'var(--text-secondary)' }}>{form.rut || '77.123.456-7'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Email Pagos:</span>
                  <span style={{ color: 'var(--accent-blue)' }}>{form.emailContacto || 'pagos@lexicore.cl'}</span>
                </div>
              </div>

              <div
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  justifyContent: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🛡️</span>
                  <strong style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                    Garantía de Certificación en Compras Públicas
                  </strong>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  Esta cuenta se encuentra validada en el registro de proveedores del Estado y en el Módulo de Facturación & Cobranza para conciliación bancaria directa de facturas y órdenes de compra con organismos públicos.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => navegar('/cobranza')}
                    style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <span>Ir a Cobranza & Facturas</span>
                    <IconoDerecha size={12} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => navegar('/')}
                    style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <span>Ver Torre de Control</span>
                    <IconoDerecha size={12} />
                  </button>
                </div>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. CALCULADORA TRIBUTARIA BIDIRECCIONAL EN TIEMPO REAL
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaCalculadoraTributaria() {
  const avisos = useAvisos();
  const [montoInput, setMontoInput] = useState<number>(1000000);
  const [modalidad, setModalidad] = useState<ModalidadMonto>('BRUTO');
  const [ivaPct, setIvaPct] = useState<number>(19);

  const calcularMut = useCalcularMontosTributarios();

  useEffect(() => {
    if (montoInput >= 0) {
      calcularMut.mutate({
        monto: Math.round(montoInput),
        modalidad,
        ivaPct,
      });
    }
  }, [montoInput, modalidad, ivaPct]);

  const copiarAlPortapapeles = (texto: string, etiqueta: string) => {
    navigator.clipboard.writeText(texto);
    avisos.exito(`${etiqueta} copiado al portapapeles`);
  };

  const resultado = calcularMut.data;

  const montosRapidos = [500000, 1000000, 2500000, 5000000, 10000000, 25000000];

  return (
    <div style={{ maxWidth: 840, margin: '0 auto' }}>
      <Panel
        titulo="Calculadora Tributaria Bidireccional en Tiempo Real"
        subtitulo="Convierte y desglosa montos Brutos a Netos o viceversa con precisión matemática para licitaciones y facturación"
      >
        {/* Chips de montos rápidos */}
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600, marginRight: 8 }}>
            Montos de prueba rápida:
          </span>
          <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            {montosRapidos.map((val) => (
              <button
                key={val}
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setMontoInput(val)}
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  fontFamily: 'var(--font-mono)',
                  borderColor: montoInput === val ? 'var(--accent-purple)' : undefined,
                }}
              >
                {formatearCLP(val)}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div className="form-group">
            <label htmlFor="calc_monto">Monto a Calcular (CLP) *</label>
            <input
              id="calc_monto"
              type="number"
              min={0}
              step={1000}
              value={montoInput}
              onChange={(e) => setMontoInput(Number(e.target.value) || 0)}
              style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="calc_mod">Modalidad Ingresada</label>
            <select
              id="calc_mod"
              value={modalidad}
              onChange={(e) => setModalidad(e.target.value as ModalidadMonto)}
              style={{ fontSize: 14, fontWeight: 600 }}
            >
              <option value="BRUTO">Monto BRUTO (Total con IVA incluido)</option>
              <option value="NETO">Monto NETO (Base imponible sin IVA)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="calc_iva">Tasa de IVA (%)</label>
            <input
              id="calc_iva"
              type="number"
              min={0}
              max={100}
              value={ivaPct}
              onChange={(e) => setIvaPct(Number(e.target.value) || 0)}
            />
          </div>
        </div>

        {resultado && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div className="calc-card-result">
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                Monto Neto (Base Imponible)
              </span>
              <strong style={{ fontSize: 20, color: 'var(--accent-blue)', fontFamily: 'var(--font-mono)' }}>
                {formatearCLP(resultado.montoNeto)}
              </strong>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => copiarAlPortapapeles(String(resultado.montoNeto), 'Monto Neto')}
                style={{ marginTop: 8 }}
              >
                <IconoCopiar size={12} />
                <span>Copiar Neto</span>
              </button>
            </div>

            <div className="calc-card-result">
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                IVA Calculado ({resultado.ivaPct}%)
              </span>
              <strong style={{ fontSize: 20, color: 'var(--accent-yellow)', fontFamily: 'var(--font-mono)' }}>
                {formatearCLP(resultado.montoIva)}
              </strong>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => copiarAlPortapapeles(String(resultado.montoIva), 'Monto IVA')}
                style={{ marginTop: 8 }}
              >
                <IconoCopiar size={12} />
                <span>Copiar IVA</span>
              </button>
            </div>

            <div className="calc-card-result">
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                Monto Bruto Total
              </span>
              <strong style={{ fontSize: 20, color: 'var(--accent-green)', fontFamily: 'var(--font-mono)' }}>
                {formatearCLP(resultado.montoBruto)}
              </strong>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => copiarAlPortapapeles(String(resultado.montoBruto), 'Monto Bruto')}
                style={{ marginTop: 8 }}
              >
                <IconoCopiar size={12} />
                <span>Copiar Bruto</span>
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 24,
            padding: '14px 18px',
            background: 'var(--bg-hover)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: 'var(--text-primary)' }}>Fórmula aplicada:</strong>{' '}
          {modalidad === 'BRUTO' ? (
            <span>
              <code>Neto = Redondeo(Bruto / (1 + {ivaPct / 100}))</code> | <code>IVA = Bruto - Neto</code>. Garantiza
              cero discrepancias con el Servicio de Impuestos Internos.
            </span>
          ) : (
            <span>
              <code>IVA = Redondeo(Neto × {ivaPct / 100})</code> | <code>Bruto = Neto + IVA</code>. Base utilizada en
              propuestas económicas y órdenes de compra oficiales.
            </span>
          )}
        </div>
      </Panel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. LÍNEAS DE NEGOCIO / CATEGORÍAS COMERCIALES (CON FILTROS 100%)
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaCategoriasComerciales({ esAdminOOwner }: { esAdminOOwner: boolean }) {
  const navegar = useNavigate();
  const avisos = useAvisos();
  const categoriasQuery = useCategoriasComercialesConfig(true);
  const crearMut = useCrearCategoriaComercial();
  const actualizarMut = useActualizarCategoriaComercial();
  const eliminarMut = useEliminarCategoriaComercial();

  // Filtros interactivos
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'TODOS' | 'ACTIVAS' | 'INACTIVAS'>('TODOS');
  const [orden, setOrden] = useState<'nombre_asc' | 'nombre_desc' | 'margen_desc' | 'margen_asc' | 'licitaciones_desc'>('nombre_asc');

  // Modales
  const [modalAbierto, setModalAbierto] = useState(false);
  const [categoriaEditando, setCategoriaEditando] = useState<any | null>(null);

  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [margenObjetivoPct, setMargenObjetivoPct] = useState(35);

  const abrirModalCrear = () => {
    setCategoriaEditando(null);
    setNombre('');
    setColor('#3b82f6');
    setMargenObjetivoPct(35);
    setModalAbierto(true);
  };

  const abrirModalEditar = (cat: any) => {
    setCategoriaEditando(cat);
    setNombre(cat.nombre);
    setColor(cat.color ?? '#3b82f6');
    setMargenObjetivoPct(cat.margenObjetivoPct ?? 35);
    setModalAbierto(true);
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (categoriaEditando) {
        await actualizarMut.mutateAsync({
          id: categoriaEditando.id,
          datos: {
            nombre: nombre.trim(),
            color,
            margenObjetivoPct: Number(margenObjetivoPct),
          },
        });
        avisos.exito('Línea de negocio actualizada con éxito.');
      } else {
        await crearMut.mutateAsync({
          nombre: nombre.trim(),
          color,
          margenObjetivoPct: Number(margenObjetivoPct),
          activa: true,
        });
        avisos.exito('Nueva línea de negocio creada con éxito.');
      }
      setModalAbierto(false);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al guardar la línea de negocio.');
    }
  };

  const alternarActiva = async (cat: any) => {
    try {
      await actualizarMut.mutateAsync({
        id: cat.id,
        datos: { activa: !cat.activa },
      });
      avisos.exito(`Línea de negocio "${cat.nombre}" ${!cat.activa ? 'activada' : 'desactivada'}.`);
    } catch (err) {
      avisos.error('No se pudo cambiar el estado de la línea comercial.');
    }
  };

  const eliminar = async (id: string, nombreCat: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar permanentemente la línea de negocio "${nombreCat}"?`)) return;
    try {
      const res = await eliminarMut.mutateAsync(id);
      avisos.exito(res?.mensaje || 'Línea de negocio eliminada definitivamente.');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo eliminar la línea de negocio.');
    }
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroEstado('TODOS');
    setOrden('nombre_asc');
  };

  if (categoriasQuery.isLoading) return <Cargando texto="Cargando líneas de negocio…" />;
  if (categoriasQuery.isError) return <ErrorCarga error={categoriasQuery.error} reintentar={() => void categoriasQuery.refetch()} />;

  const todasCategorias = categoriasQuery.data ?? [];

  // Filtrado y ordenamiento en memoria (100% interactivo)
  const categoriasFiltradas = todasCategorias.filter((cat: any) => {
    if (filtroEstado === 'ACTIVAS' && cat.activa === false) return false;
    if (filtroEstado === 'INACTIVAS' && cat.activa !== false) return false;
    if (busqueda.trim()) {
      const b = busqueda.toLowerCase().trim();
      if (!cat.nombre.toLowerCase().includes(b)) return false;
    }
    return true;
  });

  categoriasFiltradas.sort((a: any, b: any) => {
    if (orden === 'nombre_asc') return a.nombre.localeCompare(b.nombre);
    if (orden === 'nombre_desc') return b.nombre.localeCompare(a.nombre);
    if (orden === 'margen_desc') return (b.margenObjetivoPct ?? 0) - (a.margenObjetivoPct ?? 0);
    if (orden === 'margen_asc') return (a.margenObjetivoPct ?? 0) - (b.margenObjetivoPct ?? 0);
    if (orden === 'licitaciones_desc') return (b._count?.licitaciones ?? 0) - (a._count?.licitaciones ?? 0);
    return 0;
  });

  // Métricas
  const totalActivas = todasCategorias.filter((c: any) => c.activa !== false).length;
  const margenPromedio =
    todasCategorias.length > 0
      ? Math.round(
          todasCategorias.reduce((acc: number, c: any) => acc + (c.margenObjetivoPct ?? 35), 0) / todasCategorias.length,
        )
      : 35;
  const totalLicitaciones = todasCategorias.reduce((acc: number, c: any) => acc + (c._count?.licitaciones ?? 0), 0);

  const hayFiltrosActivos = busqueda.trim() !== '' || filtroEstado !== 'TODOS' || orden !== 'nombre_asc';

  return (
    <>
      {/* Banner de Sincronización con Dashboard */}
      <div
        style={{
          background: 'var(--accent-purple-soft, rgba(139, 92, 246, 0.12))',
          border: '1px solid rgba(139, 92, 246, 0.25)',
          borderRadius: 'var(--radius-sm)',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--accent-purple)' }}>Rentabilidad en Torre de Control:</strong> Los márgenes objetivos de cada línea se comparan contra los márgenes reales y el win rate en la sección <em>Rentabilidad real por línea de negocio</em> del Dashboard General.
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => navegar('/')}
          style={{
            fontSize: 11,
            padding: '4px 10px',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>Ver en Torre de Control</span>
          <IconoDerecha size={12} />
        </button>
      </div>

      {/* KPIs de Líneas de Negocio */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Líneas Comerciales"
          valor={todasCategorias.length}
          acento="var(--accent-purple)"
          nota={`${totalActivas} activas · ${todasCategorias.length - totalActivas} inactivas`}
        />
        <TarjetaKpi
          etiqueta="Margen Comercial Promedio"
          valor={`${margenPromedio}%`}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Objetivo global ponderado"
        />
        <TarjetaKpi
          etiqueta="Oportunidades Vinculadas"
          valor={totalLicitaciones}
          acento="var(--accent-blue)"
          nota="Licitaciones y proyectos asignados"
        />
        <TarjetaKpi
          etiqueta="Líneas Filtradas"
          valor={categoriasFiltradas.length}
          acento="var(--accent-yellow)"
          nota={`De un total de ${todasCategorias.length} líneas`}
        />
      </div>

      {/* Barra de Búsqueda y Filtros */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Buscador inteligente */}
          <div style={{ position: 'relative', flex: '1 1 240px', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre de línea comercial…"
              style={{
                width: '100%',
                padding: '8px 34px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 13,
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Limpiar búsqueda"
              >
                <IconoCerrar size={12} />
              </button>
            )}
          </div>

          {/* Filtro por Estado */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Estado:
            </span>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="ACTIVAS">Solo Activas</option>
              <option value="INACTIVAS">Solo Inactivas</option>
            </select>
          </div>

          {/* Ordenamiento */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Orden:
            </span>
            <select
              value={orden}
              onChange={(e) => setOrden(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="nombre_asc">Nombre (A - Z)</option>
              <option value="nombre_desc">Nombre (Z - A)</option>
              <option value="margen_desc">Margen Objetivo (Mayor a Menor)</option>
              <option value="margen_asc">Margen Objetivo (Menor a Mayor)</option>
              <option value="licitaciones_desc">Más licitaciones vinculadas</option>
            </select>
          </div>

          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={12} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* Chips de filtros activos */}
        {hayFiltrosActivos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px dashed var(--border-subtle)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros activos:</span>
            {busqueda.trim() && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-purple-soft)',
                  color: 'var(--accent-purple)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}
              >
                <span>Búsqueda: <strong>"{busqueda}"</strong></span>
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de búsqueda"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroEstado !== 'TODOS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: filtroEstado === 'ACTIVAS' ? 'var(--accent-green-soft)' : 'var(--accent-yellow-soft)',
                  color: filtroEstado === 'ACTIVAS' ? 'var(--accent-green)' : 'var(--accent-yellow)',
                  border: `1px solid ${filtroEstado === 'ACTIVAS' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                }}
              >
                <span>Estado: <strong>{filtroEstado === 'ACTIVAS' ? 'Solo Activas' : 'Solo Inactivas'}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroEstado('TODOS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de estado"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {orden !== 'nombre_asc' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-blue-soft)',
                  color: 'var(--accent-blue)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}
              >
                <span>Orden especial</span>
                <button
                  type="button"
                  onClick={() => setOrden('nombre_asc')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Volver al orden por defecto (A - Z)"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11, padding: '2px 8px', height: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={11} />
              <span>Restablecer todo</span>
            </button>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          Mostrando <strong>{categoriasFiltradas.length}</strong> de {todasCategorias.length} líneas de negocio
          {hayFiltrosActivos && ' (filtros aplicados)'}
        </div>
      </div>

      <Panel
        titulo="Líneas de Negocio & Categorías Comerciales"
        subtitulo="Define las verticales estratégicas para clasificar licitaciones, proyectos y medir márgenes objetivo"
        acciones={
          esAdminOOwner && (
            <button type="button" className="btn btn-primary btn-sm" onClick={abrirModalCrear}>
              <IconoMas size={13} />
              <span>Nueva Línea de Negocio</span>
            </button>
          )
        }
      >
        {categoriasFiltradas.length === 0 ? (
          <Vacio
            titulo={hayFiltrosActivos ? 'Sin resultados para estos filtros' : 'Sin categorías registradas'}
            detalle={
              hayFiltrosActivos
                ? 'Prueba modificando el término de búsqueda o restableciendo los filtros de estado.'
                : 'Crea tu primera línea de negocio para clasificar licitaciones y proyectos.'
            }
            accion={
              hayFiltrosActivos ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                  Restablecer filtros
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 44 }}>Color</th>
                  <th>Nombre de Línea de Negocio</th>
                  <th style={{ textAlign: 'center' }}>Margen Objetivo</th>
                  <th style={{ textAlign: 'center' }}>Oportunidades</th>
                  <th>Estado</th>
                  {esAdminOOwner && <th style={{ textAlign: 'center' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {categoriasFiltradas.map((cat: any) => (
                  <tr key={cat.id}>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: cat.color || 'var(--accent-purple)',
                          border: '2px solid rgba(255,255,255,0.25)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}
                      />
                    </td>
                    <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{cat.nombre}</td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent-green)' }}>
                      {cat.margenObjetivoPct != null ? `${cat.margenObjetivoPct}%` : '35%'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          fontSize: 11.5,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--bg-hover)',
                          color: 'var(--text-secondary)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {cat._count?.licitaciones ?? 0} licitaciones
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: cat.activa !== false ? 'rgba(16, 185, 129, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                          color: cat.activa !== false ? 'var(--accent-green)' : 'var(--text-muted)',
                        }}
                      >
                        {cat.activa !== false ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    {esAdminOOwner && (
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => abrirModalEditar(cat)}
                            title="Editar línea comercial"
                          >
                            <IconoEditar size={12} />
                            <span>Editar</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => alternarActiva(cat)}
                            title={cat.activa !== false ? 'Desactivar' : 'Activar'}
                          >
                            {cat.activa !== false ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => eliminar(cat.id, cat.nombre)}
                            style={{ color: 'var(--accent-red)' }}
                            title="Eliminar permanentemente"
                          >
                            <IconoBasura size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal Crear / Editar */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={() => setModalAbierto(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{categoriaEditando ? 'Editar Línea de Negocio' : 'Nueva Línea de Negocio'}</h2>
              <button type="button" className="btn-icono" onClick={() => setModalAbierto(false)} aria-label="Cerrar">
                <IconoCerrar size={14} />
              </button>
            </div>
            <form onSubmit={guardar}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label htmlFor="cat_nom">Nombre de la Categoría / Línea *</label>
                  <input
                    id="cat_nom"
                    type="text"
                    required
                    placeholder="Ej: Ciberseguridad & Redes"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cat_margen">Margen Comercial Objetivo (%) *</label>
                  <input
                    id="cat_margen"
                    type="number"
                    min={0}
                    max={100}
                    value={margenObjetivoPct}
                    onChange={(e) => setMargenObjetivoPct(Number(e.target.value))}
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    Margen mínimo sugerido por defecto al calcular presupuestos para esta categoría.
                  </small>
                </div>

                <div className="form-group">
                  <label>Color Identificador</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                    {PALETA_COLORES_SUGERIDOS.map((hex) => (
                      <button
                        key={hex}
                        type="button"
                        onClick={() => setColor(hex)}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: hex,
                          border: color === hex ? '2.5px solid var(--text-primary)' : '2px solid transparent',
                          boxShadow: color === hex ? '0 0 0 2px var(--bg-card)' : 'none',
                          cursor: 'pointer',
                          transition: '0.15s',
                        }}
                        aria-label={`Seleccionar color ${hex}`}
                      />
                    ))}
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      style={{ width: 34, height: 34, padding: 0, cursor: 'pointer', background: 'transparent', border: 'none' }}
                      title="Elegir color personalizado"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalAbierto(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={crearMut.isPending || actualizarMut.isPending}>
                  {crearMut.isPending || actualizarMut.isPending ? 'Guardando…' : 'Guardar Categoría'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. CATEGORÍAS DE COSTO & CENTROS DE GASTO (CON FILTROS 100%)
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaCategoriasGasto({ esAdminOOwner }: { esAdminOOwner: boolean }) {
  const avisos = useAvisos();
  const navegar = useNavigate();
  const categoriasGastoQuery = useCategoriasGastoConfig(true);
  const crearMut = useCrearCategoriaGastoConfig();
  const actualizarMut = useActualizarCategoriaGastoConfig();
  const eliminarMut = useEliminarCategoriaGastoConfig();

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<'TODOS' | 'DIRECTO' | 'INDIRECTO'>('TODOS');
  const [filtroPersonal, setFiltroPersonal] = useState<'TODOS' | 'PERSONAL' | 'INSUMOS'>('TODOS');
  const [filtroEstado, setFiltroEstado] = useState<'TODOS' | 'ACTIVAS' | 'INACTIVAS'>('TODOS');

  // Modales
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<any | null>(null);

  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<'DIRECTO' | 'INDIRECTO'>('DIRECTO');
  const [esCostoPersonal, setEsCostoPersonal] = useState(false);

  const abrirModalCrear = () => {
    setEditando(null);
    setNombre('');
    setTipo('DIRECTO');
    setEsCostoPersonal(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (cat: any) => {
    setEditando(cat);
    setNombre(cat.nombre);
    setTipo(cat.tipo === 'INDIRECTO' ? 'INDIRECTO' : 'DIRECTO');
    setEsCostoPersonal(Boolean(cat.esCostoPersonal));
    setModalAbierto(true);
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editando) {
        await actualizarMut.mutateAsync({
          id: editando.id,
          datos: {
            nombre: nombre.trim(),
            tipo,
            esCostoPersonal,
          },
        });
        avisos.exito('Categoría de costo actualizada.');
      } else {
        await crearMut.mutateAsync({
          nombre: nombre.trim(),
          tipo,
          esCostoPersonal,
          activa: true,
        });
        avisos.exito('Nueva categoría de costo creada.');
      }
      setModalAbierto(false);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al guardar categoría de gasto.');
    }
  };

  const alternarActiva = async (cat: any) => {
    try {
      await actualizarMut.mutateAsync({
        id: cat.id,
        datos: { activa: !cat.activa },
      });
      avisos.exito(`Categoría de costo ${!cat.activa ? 'activada' : 'desactivada'}.`);
    } catch (err) {
      avisos.error('No se pudo cambiar el estado.');
    }
  };

  const eliminar = async (id: string, nombreCat: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar permanentemente la categoría de costo "${nombreCat}"?`)) return;
    try {
      const res = await eliminarMut.mutateAsync(id);
      avisos.exito(res?.mensaje || 'Categoría de costo eliminada con éxito.');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo eliminar la categoría de costo.');
    }
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroTipo('TODOS');
    setFiltroPersonal('TODOS');
    setFiltroEstado('TODOS');
  };

  if (categoriasGastoQuery.isLoading) return <Cargando texto="Cargando categorías de costos…" />;
  if (categoriasGastoQuery.isError) return <ErrorCarga error={categoriasGastoQuery.error} reintentar={() => void categoriasGastoQuery.refetch()} />;

  const todas = categoriasGastoQuery.data ?? [];

  // Filtrado 100%
  const filtradas = todas.filter((cat: any) => {
    if (filtroTipo !== 'TODOS' && cat.tipo !== filtroTipo) return false;
    if (filtroPersonal === 'PERSONAL' && !cat.esCostoPersonal) return false;
    if (filtroPersonal === 'INSUMOS' && cat.esCostoPersonal) return false;
    if (filtroEstado === 'ACTIVAS' && cat.activa === false) return false;
    if (filtroEstado === 'INACTIVAS' && cat.activa !== false) return false;
    if (busqueda.trim()) {
      const b = busqueda.toLowerCase().trim();
      if (!cat.nombre.toLowerCase().includes(b)) return false;
    }
    return true;
  });

  const totalDirectas = todas.filter((c: any) => c.tipo === 'DIRECTO').length;
  const totalIndirectas = todas.filter((c: any) => c.tipo === 'INDIRECTO').length;
  const totalPersonal = todas.filter((c: any) => Boolean(c.esCostoPersonal)).length;

  const hayFiltrosActivos =
    busqueda.trim() !== '' || filtroTipo !== 'TODOS' || filtroPersonal !== 'TODOS' || filtroEstado !== 'TODOS';

  return (
    <>
      {/* KPIs de Centros de Costo */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Catálogo de Costos"
          valor={todas.length}
          acento="var(--accent-purple)"
          nota={`${todas.filter((c: any) => c.activa !== false).length} categorías activas`}
        />
        <TarjetaKpi
          etiqueta="Costos Directos (Proyectos)"
          valor={totalDirectas}
          acento="var(--accent-blue)"
          colorValor="var(--accent-blue)"
          nota="Imputables a contratos y obras"
        />
        <TarjetaKpi
          etiqueta="Gastos Indirectos (Empresa)"
          valor={totalIndirectas}
          acento="var(--accent-yellow)"
          colorValor="var(--accent-yellow)"
          nota="Arriendos, licencias fijas, suministros"
        />
        <TarjetaKpi
          etiqueta="Costos de Personal / RRHH"
          valor={totalPersonal}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Sueldos, cuadrillas y honorarios"
        />
      </div>

      {/* Banner de Sincronización con Módulo de Proyectos */}
      <div
        style={{
          background: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.25)',
          borderRadius: 'var(--radius-md)',
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
          <span style={{ fontSize: 18 }}>💼</span>
          <div>
            <strong style={{ color: 'var(--text-primary)' }}>Sincronización con el Módulo de Proyectos:</strong>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Las categorías de costo tipo <strong>DIRECTO</strong> alimentan las líneas de presupuesto y gastos reales imputables en la cartera de proyectos.
            </div>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => navegar('/proyectos')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
        >
          <span>Ir a Cartera de Proyectos</span>
          <span>→</span>
        </button>
      </div>

      {/* Barra de Búsqueda y Filtros */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Buscador */}
          <div style={{ position: 'relative', flex: '1 1 240px', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre de costo o concepto…"
              style={{
                width: '100%',
                padding: '8px 34px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 13,
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Limpiar búsqueda"
              >
                <IconoCerrar size={12} />
              </button>
            )}
          </div>

          {/* Filtro por Tipo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Tipo:
            </span>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODOS">Todos los tipos</option>
              <option value="DIRECTO">Directo (A Proyecto)</option>
              <option value="INDIRECTO">Indirecto (Gasto Empresa)</option>
            </select>
          </div>

          {/* Filtro Personal / RRHH */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Naturaleza:
            </span>
            <select
              value={filtroPersonal}
              onChange={(e) => setFiltroPersonal(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODOS">Todas las naturalezas</option>
              <option value="PERSONAL">Solo Personal / RRHH</option>
              <option value="INSUMOS">Solo Insumos / Servicios</option>
            </select>
          </div>

          {/* Filtro Estado */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Estado:
            </span>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="ACTIVAS">Solo Activas</option>
              <option value="INACTIVAS">Solo Inactivas</option>
            </select>
          </div>

          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={12} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* Chips de filtros activos */}
        {hayFiltrosActivos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px dashed var(--border-subtle)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros activos:</span>
            {busqueda.trim() && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-purple-soft)',
                  color: 'var(--accent-purple)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}
              >
                <span>Búsqueda: <strong>"{busqueda}"</strong></span>
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de búsqueda"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroTipo !== 'TODOS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-blue-soft)',
                  color: 'var(--accent-blue)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}
              >
                <span>Tipo: <strong>{filtroTipo === 'DIRECTO' ? 'Directo (Proyectos)' : 'Indirecto (Empresa)'}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroTipo('TODOS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de tipo"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroPersonal !== 'TODOS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-green-soft)',
                  color: 'var(--accent-green)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                }}
              >
                <span>Naturaleza: <strong>{filtroPersonal === 'PERSONAL' ? 'Personal / RRHH' : 'Insumos / Servicios'}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroPersonal('TODOS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de naturaleza"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroEstado !== 'TODOS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: filtroEstado === 'ACTIVAS' ? 'var(--accent-green-soft)' : 'var(--accent-yellow-soft)',
                  color: filtroEstado === 'ACTIVAS' ? 'var(--accent-green)' : 'var(--accent-yellow)',
                  border: `1px solid ${filtroEstado === 'ACTIVAS' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                }}
              >
                <span>Estado: <strong>{filtroEstado === 'ACTIVAS' ? 'Solo Activas' : 'Solo Inactivas'}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroEstado('TODOS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de estado"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11, padding: '2px 8px', height: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={11} />
              <span>Restablecer todo</span>
            </button>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          Mostrando <strong>{filtradas.length}</strong> de {todas.length} categorías de costo
          {hayFiltrosActivos && ' (filtros aplicados)'}
        </div>
      </div>

      <Panel
        titulo="Catálogo de Costos & Centros de Gasto"
        subtitulo="Clasificación de egresos para distinguir costos directos imputables a proyectos de gastos generales de la empresa"
        acciones={
          esAdminOOwner && (
            <button type="button" className="btn btn-primary btn-sm" onClick={abrirModalCrear}>
              <IconoMas size={13} />
              <span>Nueva Categoría de Costo</span>
            </button>
          )
        }
      >
        {filtradas.length === 0 ? (
          <Vacio
            titulo={hayFiltrosActivos ? 'Sin resultados para estos filtros' : 'Sin categorías de gasto'}
            detalle={
              hayFiltrosActivos
                ? 'Prueba modificando los filtros de tipo, personal o el texto buscado.'
                : 'Crea categorías para registrar compras y egresos.'
            }
            accion={
              hayFiltrosActivos ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                  Restablecer filtros
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Nombre del Costo</th>
                  <th>Tipo de Imputación</th>
                  <th>Costo de Personal</th>
                  <th style={{ textAlign: 'center' }}>Gastos Registrados</th>
                  <th>Estado</th>
                  {esAdminOOwner && <th style={{ textAlign: 'center' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filtradas.map((cat: any) => (
                  <tr key={cat.id}>
                    <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{cat.nombre}</td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: cat.tipo === 'DIRECTO' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                          color: cat.tipo === 'DIRECTO' ? 'var(--accent-blue)' : 'var(--accent-yellow)',
                        }}
                      >
                        {cat.tipo === 'DIRECTO' ? 'DIRECTO (A Proyecto)' : 'INDIRECTO (Gasto Empresa)'}
                      </span>
                    </td>
                    <td>
                      {cat.esCostoPersonal ? (
                        <span style={{ fontSize: 12, color: 'var(--accent-green)', fontWeight: 600 }}>
                          ✓ Sí (RRHH / Sueldos)
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No (Insumos / Servicios)</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          fontSize: 11.5,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--bg-hover)',
                          color: 'var(--text-secondary)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {cat._count?.gastos ?? 0} gastos
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: cat.activa !== false ? 'rgba(16, 185, 129, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                          color: cat.activa !== false ? 'var(--accent-green)' : 'var(--text-muted)',
                        }}
                      >
                        {cat.activa !== false ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    {esAdminOOwner && (
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => abrirModalEditar(cat)}
                            title="Editar"
                          >
                            <IconoEditar size={12} />
                            <span>Editar</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => alternarActiva(cat)}
                            title={cat.activa !== false ? 'Desactivar' : 'Activar'}
                          >
                            {cat.activa !== false ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => eliminar(cat.id, cat.nombre)}
                            style={{ color: 'var(--accent-red)' }}
                            title="Eliminar"
                          >
                            <IconoBasura size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal Crear / Editar */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={() => setModalAbierto(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Categoría de Costo' : 'Nueva Categoría de Costo'}</h2>
              <button type="button" className="btn-icono" onClick={() => setModalAbierto(false)} aria-label="Cerrar">
                <IconoCerrar size={14} />
              </button>
            </div>
            <form onSubmit={guardar}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label htmlFor="catg_nom">Nombre de la Categoría *</label>
                  <input
                    id="catg_nom"
                    type="text"
                    required
                    placeholder="Ej: Licencias Cloud, Viáticos, Honorarios"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="catg_tipo">Tipo de Costo</label>
                  <select
                    id="catg_tipo"
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value as 'DIRECTO' | 'INDIRECTO')}
                  >
                    <option value="DIRECTO">DIRECTO (Imputable a Proyectos y Contratos)</option>
                    <option value="INDIRECTO">INDIRECTO (Gasto Fijo / General de la Empresa)</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    id="catg_rrhh"
                    type="checkbox"
                    checked={esCostoPersonal}
                    onChange={(e) => setEsCostoPersonal(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <label htmlFor="catg_rrhh" style={{ fontSize: 13, cursor: 'pointer', margin: 0 }}>
                    Es costo de personal / Recursos Humanos (Sueldos, cuadrilla, honorarios)
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalAbierto(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={crearMut.isPending || actualizarMut.isPending}>
                  {crearMut.isPending || actualizarMut.isPending ? 'Guardando…' : 'Guardar Categoría'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. CALIBRACIÓN DEL PREDICTOR DE PRECIOS E INTELIGENCIA ARTIFICIAL
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaCalibracionPredictor({ esAdminOOwner }: { esAdminOOwner: boolean }) {
  const navigate = useNavigate();
  const avisos = useAvisos();
  const predictorQuery = useParametrosPredictorConfig();
  const consolaQuery = useConsolaMotor();
  const actualizarMut = useActualizarParametrosPredictorConfig();
  const restablecerMut = useRestablecerParametrosPredictorConfig();
  const aplicarEstrategiaMut = useAplicarEstrategiaMotor();

  const [form, setForm] = useState({
    agresividadPrecio: 1.0,
    agresividadPlazo: 1.0,
    margenMinimoPct: 20,
    plazoMinimoDias: 15,
    vidaMediaMeses: 18,
    pesoMismoOrganismo: 2.0,
    pesoMismoTipoOrganismo: 1.0,
    pesoMismoTipoProceso: 1.0,
  });

  const [mostrarPesosAvanzados, setMostrarPesosAvanzados] = useState(false);

  useEffect(() => {
    if (predictorQuery.data) {
      const p = predictorQuery.data;
      setForm({
        agresividadPrecio: p.agresividadPrecio ?? 1.0,
        agresividadPlazo: p.agresividadPlazo ?? 1.0,
        margenMinimoPct: p.margenMinimoPct ?? 20,
        plazoMinimoDias: p.plazoMinimoDias ?? 15,
        vidaMediaMeses: p.vidaMediaMeses ?? 18,
        pesoMismoOrganismo: p.pesoMismoOrganismo ?? 2.0,
        pesoMismoTipoOrganismo: p.pesoMismoTipoOrganismo ?? 1.0,
        pesoMismoTipoProceso: p.pesoMismoTipoProceso ?? 1.0,
      });
    }
  }, [predictorQuery.data]);

  const aplicarEstrategia = async (modo: ModoEstrategiaPredictor) => {
    if (!esAdminOOwner) return;
    try {
      await aplicarEstrategiaMut.mutateAsync({ modo });
      const cfg = ESTRATEGIAS_PREDICTOR[modo];
      setForm((prev) => ({
        ...prev,
        agresividadPrecio: cfg.parametros.agresividadPrecio,
        agresividadPlazo: cfg.parametros.agresividadPlazo,
        margenMinimoPct: cfg.parametros.margenMinimoPct,
      }));
      await Promise.all([consolaQuery.refetch(), predictorQuery.refetch()]);
      avisos.exito(`Estrategia ${cfg.nombre} aplicada al motor IA exitosamente.`);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al aplicar estrategia.');
    }
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!esAdminOOwner) return;
    try {
      await actualizarMut.mutateAsync({
        agresividadPrecio: Number(form.agresividadPrecio),
        agresividadPlazo: Number(form.agresividadPlazo),
        margenMinimoPct: Number(form.margenMinimoPct),
        plazoMinimoDias: Number(form.plazoMinimoDias),
        vidaMediaMeses: Number(form.vidaMediaMeses),
        pesoMismoOrganismo: Number(form.pesoMismoOrganismo),
        pesoMismoTipoOrganismo: Number(form.pesoMismoTipoOrganismo),
        pesoMismoTipoProceso: Number(form.pesoMismoTipoProceso),
      });
      await consolaQuery.refetch();
      avisos.exito('Calibración del motor de predicción IA guardada exitosamente.');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al actualizar predictor.');
    }
  };

  const restablecer = async () => {
    if (!window.confirm('¿Restablecer todos los parámetros del predictor a los valores óptimos de fábrica?')) return;
    try {
      await restablecerMut.mutateAsync();
      await Promise.all([consolaQuery.refetch(), predictorQuery.refetch()]);
      avisos.exito('Parámetros del predictor restablecidos a valores de fábrica.');
    } catch (err) {
      avisos.error('No se pudo restablecer la configuración.');
    }
  };

  if (predictorQuery.isLoading || consolaQuery.isLoading) {
    return <Cargando texto="Cargando calibración y telemetría del motor predictivo…" />;
  }
  if (predictorQuery.isError) {
    return <ErrorCarga error={predictorQuery.error} reintentar={() => void predictorQuery.refetch()} />;
  }

  const metricas = consolaQuery.data?.kpis;
  const estrategiaActual = consolaQuery.data?.diagnostico?.estrategiaActiva ?? 'EQUILIBRADO';

  return (
    <>
      {/* Banner de Acceso Directo al Motor Predictivo y Simulador */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.25)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'var(--accent-purple)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
            }}
          >
            <IconoMotorIA size={22} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              Consola Avanzada del Motor Predictivo & Simulador
              <span className="badge badge-accent" style={{ fontSize: 10, padding: '2px 7px' }}>V2 INTELIGENTE</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              Simula ofertas en vivo, analiza licitaciones previas en la tabla &quot;¿Habríamos ganado?&quot; y realiza test de impacto antes de calibrar.
            </div>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/motor')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', fontWeight: 700 }}
        >
          <IconoCerebro size={16} />
          <span>Abrir Consola de Motor Predictivo ➔</span>
        </button>
      </div>

      {/* Tarjetas KPI de Telemetría Real */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Precisión Global Histórica"
          valor={metricas ? `${metricas.precisionGlobalPct.toFixed(1)}%` : '---'}
          acento="var(--accent-purple)"
          colorValor="var(--accent-purple)"
          nota={metricas ? `${metricas.licitacionesUtiles} licitaciones evaluadas` : 'Sin datos suficientes'}
        />
        <TarjetaKpi
          etiqueta="Error Medio (MAPE)"
          valor={metricas ? `${metricas.errorMedioPct.toFixed(1)}%` : '---'}
          acento="var(--accent-blue)"
          colorValor="var(--accent-blue)"
          nota="Desviación media de precios adjudicados"
        />
        <TarjetaKpi
          etiqueta="Piso Mínimo Protegido"
          valor={`${form.margenMinimoPct}%`}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Margen de rentabilidad intocable"
        />
        <TarjetaKpi
          etiqueta="Estrategia Activa"
          valor={estrategiaActual}
          acento="var(--accent-yellow)"
          nota={`Sensibilidad actual: ${form.agresividadPrecio.toFixed(2)}x`}
        />
      </div>

      <Panel
        titulo="Calibración del Motor de Precios & Inteligencia Artificial"
        subtitulo="Ajusta la agresividad competitiva, márgenes protegidos y ponderación histórica del recomendador"
        acciones={
          esAdminOOwner && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={restablecer}
                disabled={restablecerMut.isPending}
              >
                <IconoRestablecer size={13} />
                <span>Restablecer de Fábrica</span>
              </button>
            </div>
          )
        }
      >
        {/* Selector Rápido de Estrategias en 1 Clic */}
        <div style={{ background: 'var(--bg-hover)', padding: 18, borderRadius: 'var(--radius-md)', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                Estrategia Competitiva en 1 Clic
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Aplica una postura comercial precalibrada con un solo clic.
              </div>
            </div>
            <span className="badge badge-purple" style={{ fontSize: 11, padding: '4px 10px', textTransform: 'uppercase' }}>
              Modo Activo: {estrategiaActual}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {(['AGRESIVO', 'EQUILIBRADO', 'CONSERVADOR'] as ModoEstrategiaPredictor[]).map((modo) => {
              const cfg = ESTRATEGIAS_PREDICTOR[modo];
              const esActivo = estrategiaActual === modo;
              return (
                <button
                  key={modo}
                  type="button"
                  onClick={() => void aplicarEstrategia(modo)}
                  disabled={!esAdminOOwner || aplicarEstrategiaMut.isPending}
                  className={`btn ${esActivo ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 6,
                    padding: '14px 16px',
                    textAlign: 'left',
                    height: 'auto',
                    border: esActivo ? '2px solid var(--accent-purple)' : undefined,
                    cursor: esAdminOOwner ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <strong style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {modo === 'AGRESIVO' && '⚡'}
                      {modo === 'EQUILIBRADO' && '🎯'}
                      {modo === 'CONSERVADOR' && '🛡️'}
                      {cfg.nombre}
                    </strong>
                    {esActivo && (
                      <span style={{ fontSize: 10, background: 'var(--accent-purple)', color: '#fff', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                        ACTIVO
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 11.5, opacity: 0.85, fontWeight: 'normal', lineHeight: 1.35 }}>
                    {cfg.descripcion}
                  </span>
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8, fontFamily: 'var(--font-mono)' }}>
                    Margen mín: {cfg.parametros.margenMinimoPct}% · Sensibilidad: {cfg.parametros.agresividadPrecio.toFixed(2)}x
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Formulario de Calibración Fina */}
        <form onSubmit={guardar} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            <div style={{ background: 'var(--bg-hover)', padding: 16, borderRadius: 'var(--radius-md)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label htmlFor="p_agr_precio" style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)' }}>
                  Agresividad de Precio
                </label>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-purple)', fontSize: 15 }}>
                  {form.agresividadPrecio.toFixed(2)}x
                </strong>
              </div>
              <input
                id="p_agr_precio"
                type="range"
                min={0.5}
                max={1.5}
                step={0.05}
                value={form.agresividadPrecio}
                onChange={(e) => setForm((f) => ({ ...f, agresividadPrecio: Number(e.target.value) }))}
                className="slider-ia-control"
                disabled={!esAdminOOwner}
              />
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 }}>
                {form.agresividadPrecio < 1
                  ? '📉 Oferta agresiva: sugiere precios con descuento para aumentar la probabilidad matemática de adjudicación.'
                  : form.agresividadPrecio > 1
                  ? '📈 Oferta conservadora: prioriza maximizar el margen de utilidad y beneficio sobre el volumen de ventas.'
                  : '⚖️ Oferta balanceada: equilibrio exacto entre probabilidad matemática de victoria y rentabilidad esperada.'}
              </div>
            </div>

            <div style={{ background: 'var(--bg-hover)', padding: 16, borderRadius: 'var(--radius-md)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label htmlFor="p_agr_plazo" style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)' }}>
                  Agresividad de Plazo
                </label>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-blue)', fontSize: 15 }}>
                  {form.agresividadPlazo.toFixed(2)}x
                </strong>
              </div>
              <input
                id="p_agr_plazo"
                type="range"
                min={0.5}
                max={1.5}
                step={0.05}
                value={form.agresividadPlazo}
                onChange={(e) => setForm((f) => ({ ...f, agresividadPlazo: Number(e.target.value) }))}
                className="slider-ia-control"
                disabled={!esAdminOOwner}
              />
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 }}>
                {form.agresividadPlazo < 1
                  ? '⚡ Plazos reducidos y veloces para licitaciones con alta ponderación de tiempo de entrega.'
                  : '🛡️ Plazos holgados con colchón de seguridad operativo para evitar multas por atraso.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div className="form-group">
              <label htmlFor="p_margen">Margen Mínimo Protegido (%)</label>
              <input
                id="p_margen"
                type="number"
                min={0}
                max={80}
                value={form.margenMinimoPct}
                onChange={(e) => setForm((f) => ({ ...f, margenMinimoPct: Number(e.target.value) }))}
                disabled={!esAdminOOwner}
              />
              <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                El predictor jamás sugerirá un precio que deje menos de este margen (Guardarraíl inviolable).
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="p_plazomin">Plazo Mínimo (Días)</label>
              <input
                id="p_plazomin"
                type="number"
                min={1}
                max={60}
                value={form.plazoMinimoDias}
                onChange={(e) => setForm((f) => ({ ...f, plazoMinimoDias: Number(e.target.value) }))}
                disabled={!esAdminOOwner}
              />
              <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Límite inferior de días hábiles ofertables.</small>
            </div>

            <div className="form-group">
              <label htmlFor="p_vidamedia">Vida Media Histórica (Meses)</label>
              <input
                id="p_vidamedia"
                type="number"
                min={1}
                max={36}
                value={form.vidaMediaMeses}
                onChange={(e) => setForm((f) => ({ ...f, vidaMediaMeses: Number(e.target.value) }))}
                disabled={!esAdminOOwner}
              />
              <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                Antigüedad a la que un dato histórico pierde el 50% de su peso probabilístico.
              </small>
            </div>
          </div>

          {/* Sección desplegable de Ponderaciones Avanzadas */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setMostrarPesosAvanzados(!mostrarPesosAvanzados)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span>{mostrarPesosAvanzados ? '▲ Ocultar Ponderaciones Avanzadas' : '▼ Mostrar Ponderaciones Avanzadas del Algoritmo'}</span>
            </button>

            {mostrarPesosAvanzados && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16, background: 'var(--bg-hover)', padding: 16, borderRadius: 'var(--radius-md)' }}>
                <div className="form-group">
                  <label htmlFor="p_peso_org">Ponderación Mismo Organismo ({form.pesoMismoOrganismo.toFixed(1)}x)</label>
                  <input
                    id="p_peso_org"
                    type="range"
                    min={0.5}
                    max={5.0}
                    step={0.1}
                    value={form.pesoMismoOrganismo}
                    onChange={(e) => setForm((f) => ({ ...f, pesoMismoOrganismo: Number(e.target.value) }))}
                    disabled={!esAdminOOwner}
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Importancia relativa cuando el comprador es el mismo organismo público.</small>
                </div>

                <div className="form-group">
                  <label htmlFor="p_peso_tipo_org">Ponderación Mismo Tipo Org. ({form.pesoMismoTipoOrganismo.toFixed(1)}x)</label>
                  <input
                    id="p_peso_tipo_org"
                    type="range"
                    min={0.5}
                    max={5.0}
                    step={0.1}
                    value={form.pesoMismoTipoOrganismo}
                    onChange={(e) => setForm((f) => ({ ...f, pesoMismoTipoOrganismo: Number(e.target.value) }))}
                    disabled={!esAdminOOwner}
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Importancia cuando pertenece al mismo segmento (salud, municipios, ministerios).</small>
                </div>

                <div className="form-group">
                  <label htmlFor="p_peso_proc">Ponderación Mismo Tipo Proceso ({form.pesoMismoTipoProceso.toFixed(1)}x)</label>
                  <input
                    id="p_peso_proc"
                    type="range"
                    min={0.5}
                    max={5.0}
                    step={0.1}
                    value={form.pesoMismoTipoProceso}
                    onChange={(e) => setForm((f) => ({ ...f, pesoMismoTipoProceso: Number(e.target.value) }))}
                    disabled={!esAdminOOwner}
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Importancia cuando coincide la modalidad de licitación (L1, LE, LP, etc.).</small>
                </div>
              </div>
            )}
          </div>

          {esAdminOOwner && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={actualizarMut.isPending}>
                {actualizarMut.isPending ? 'Guardando calibración…' : 'Guardar Calibración del Predictor'}
              </button>
            </div>
          )}
        </form>
      </Panel>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. EQUIPO DE TRABAJO & CONTROL DE ACCESO (CON FILTROS 100%)
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaUsuariosEmpresa({ esAdminOOwner }: { esAdminOOwner: boolean }) {
  const avisos = useAvisos();
  const { usuario: usuarioActual } = useAuth();
  const usuariosQuery = useUsuariosEmpresa();
  const crearMut = useCrearUsuarioEmpresa();
  const actualizarMut = useActualizarUsuarioEmpresa();
  const eliminarUsuarioMut = useEliminarUsuarioEmpresa();

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState<string>('TODOS');
  const [filtroEstado, setFiltroEstado] = useState<'TODOS' | 'ACTIVOS' | 'SUSPENDIDOS'>('TODOS');

  // Modales
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false);
  const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState<any | null>(null);

  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoEmail, setNuevoEmail] = useState('');
  const [nuevoPassword, setNuevoPassword] = useState('');
  const [nuevoRol, setNuevoRol] = useState<Rol>('GESTOR');

  const [editNombre, setEditNombre] = useState('');
  const [editRol, setEditRol] = useState<Rol>('GESTOR');
  const [editActivo, setEditActivo] = useState(true);

  const abrirCrear = () => {
    setNuevoNombre('');
    setNuevoEmail('');
    setNuevoPassword('');
    setNuevoRol('GESTOR');
    setModalCrearAbierto(true);
  };

  const abrirEditar = (u: any) => {
    setUsuarioEditando(u);
    setEditNombre(u.nombre);
    setEditRol(u.rol as Rol);
    setEditActivo(Boolean(u.activo));
    setModalEditarAbierto(true);
  };

  const guardarCrear = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await crearMut.mutateAsync({
        nombre: nuevoNombre.trim(),
        email: nuevoEmail.trim(),
        password: nuevoPassword,
        rol: nuevoRol,
      });
      avisos.exito(`Usuario ${nuevoNombre} incorporado correctamente al equipo.`);
      setModalCrearAbierto(false);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al crear usuario.');
    }
  };

  const guardarEditar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioEditando) return;
    try {
      await actualizarMut.mutateAsync({
        id: usuarioEditando.id,
        datos: {
          nombre: editNombre.trim(),
          rol: editRol,
          activo: editActivo,
        },
      });
      avisos.exito('Usuario y permisos actualizados correctamente.');
      setModalEditarAbierto(false);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al actualizar usuario.');
    }
  };

  const eliminarUsuario = async (u: any) => {
    if (usuarioActual?.id === u.id) {
      avisos.error('No puedes eliminar tu propia cuenta de usuario mientras estás conectado.');
      return;
    }
    if (
      !window.confirm(
        `¿Estás seguro de eliminar permanentemente al usuario "${u.nombre}" (${u.email})? Esta acción es irreversible.`,
      )
    ) {
      return;
    }
    try {
      const res = await eliminarUsuarioMut.mutateAsync(u.id);
      avisos.exito(res?.mensaje || `Usuario "${u.nombre}" eliminado definitivamente.`);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo eliminar el usuario.');
    }
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroRol('TODOS');
    setFiltroEstado('TODOS');
  };

  if (usuariosQuery.isLoading) return <Cargando texto="Cargando usuarios de la empresa…" />;
  if (usuariosQuery.isError) return <ErrorCarga error={usuariosQuery.error} reintentar={() => void usuariosQuery.refetch()} />;

  const todosUsuarios = usuariosQuery.data ?? [];

  // Filtrado 100%
  const usuariosFiltrados = todosUsuarios.filter((u: any) => {
    if (filtroRol !== 'TODOS' && u.rol !== filtroRol) return false;
    if (filtroEstado === 'ACTIVOS' && !u.activo) return false;
    if (filtroEstado === 'SUSPENDIDOS' && u.activo) return false;
    if (busqueda.trim()) {
      const b = busqueda.toLowerCase().trim();
      if (!u.nombre.toLowerCase().includes(b) && !u.email.toLowerCase().includes(b)) return false;
    }
    return true;
  });

  const totalActivos = todosUsuarios.filter((u: any) => u.activo).length;
  const totalAdmins = todosUsuarios.filter((u: any) => u.rol === 'ADMIN' || u.rol === 'OWNER').length;
  const totalGestores = todosUsuarios.filter((u: any) => u.rol === 'GESTOR').length;

  const hayFiltrosActivos = busqueda.trim() !== '' || filtroRol !== 'TODOS' || filtroEstado !== 'TODOS';

  return (
    <>
      {/* KPIs del Equipo */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Equipo de Trabajo"
          valor={todosUsuarios.length}
          acento="var(--accent-purple)"
          nota={`${totalActivos} activos · ${todosUsuarios.length - totalActivos} suspendidos`}
        />
        <TarjetaKpi
          etiqueta="Colaboradores Activos"
          valor={totalActivos}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Con acceso habilitado al sistema"
        />
        <TarjetaKpi
          etiqueta="Dueños & Administradores"
          valor={totalAdmins}
          acento="var(--accent-yellow)"
          colorValor="var(--accent-yellow)"
          nota="Roles con privilegios totales"
        />
        <TarjetaKpi
          etiqueta="Gestores Comerciales"
          valor={totalGestores}
          acento="var(--accent-blue)"
          nota="Operadores de licitaciones y proyectos"
        />
      </div>

      {/* Barra de Búsqueda y Filtros */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Buscador */}
          <div style={{ position: 'relative', flex: '1 1 240px', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar colaborador por nombre o email…"
              style={{
                width: '100%',
                padding: '8px 34px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 13,
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Limpiar búsqueda"
              >
                <IconoCerrar size={12} />
              </button>
            )}
          </div>

          {/* Filtro Rol */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Rol:
            </span>
            <select
              value={filtroRol}
              onChange={(e) => setFiltroRol(e.target.value)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODOS">Todos los roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ETIQUETAS_ROL[r]}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Estado */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Estado:
            </span>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="ACTIVOS">Solo Activos</option>
              <option value="SUSPENDIDOS">Solo Suspendidos</option>
            </select>
          </div>

          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={12} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* Chips de filtros activos */}
        {hayFiltrosActivos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px dashed var(--border-subtle)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros activos:</span>
            {busqueda.trim() && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-purple-soft)',
                  color: 'var(--accent-purple)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}
              >
                <span>Búsqueda: <strong>"{busqueda}"</strong></span>
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de búsqueda"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroRol !== 'TODOS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-blue-soft)',
                  color: 'var(--accent-blue)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}
              >
                <span>Rol: <strong>{ETIQUETAS_ROL[filtroRol as Rol] ?? filtroRol}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroRol('TODOS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de rol"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroEstado !== 'TODOS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: filtroEstado === 'ACTIVOS' ? 'var(--accent-green-soft)' : 'var(--accent-yellow-soft)',
                  color: filtroEstado === 'ACTIVOS' ? 'var(--accent-green)' : 'var(--accent-yellow)',
                  border: `1px solid ${filtroEstado === 'ACTIVOS' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                }}
              >
                <span>Estado: <strong>{filtroEstado === 'ACTIVOS' ? 'Solo Activos' : 'Solo Suspendidos'}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroEstado('TODOS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de estado"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11, padding: '2px 8px', height: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={11} />
              <span>Restablecer todo</span>
            </button>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          Mostrando <strong>{usuariosFiltrados.length}</strong> de {todosUsuarios.length} usuarios
          {hayFiltrosActivos && ' (filtros aplicados)'}
        </div>
      </div>

      <Panel
        titulo="Equipo de Trabajo & Control de Acceso (RBAC)"
        subtitulo="Administra a los miembros de la empresa, sus permisos y estado de acceso"
        acciones={
          esAdminOOwner && (
            <button type="button" className="btn btn-primary btn-sm" onClick={abrirCrear}>
              <IconoMas size={13} />
              <span>Nuevo Usuario</span>
            </button>
          )
        }
      >
        {usuariosFiltrados.length === 0 ? (
          <Vacio
            titulo={hayFiltrosActivos ? 'Sin usuarios para estos filtros' : 'Sin usuarios registrados'}
            detalle={
              hayFiltrosActivos
                ? 'Prueba con otro rol, estado o término de búsqueda.'
                : 'Crea colaboradores para darles acceso a la plataforma.'
            }
            accion={
              hayFiltrosActivos ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                  Restablecer filtros
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Email</th>
                  <th>Rol Asignado</th>
                  <th>Estado</th>
                  <th>Último Acceso</th>
                  {esAdminOOwner && <th style={{ textAlign: 'center' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map((u: any) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: 'var(--bg-hover)',
                            border: '1px solid var(--border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 12.5,
                            color: 'var(--text-primary)',
                          }}
                        >
                          {u.nombre.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>{u.nombre}</span>
                            {usuarioActual?.id === u.id && (
                              <span
                                style={{
                                  fontSize: 10,
                                  padding: '1px 6px',
                                  borderRadius: 'var(--radius-full)',
                                  background: 'var(--accent-purple-soft)',
                                  color: 'var(--accent-purple)',
                                  fontWeight: 700,
                                }}
                              >
                                Tú
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{u.email}</td>
                    <td>
                      <span className={`badge-rol badge-rol-${u.rol}`}>{ETIQUETAS_ROL[u.rol as Rol] ?? u.rol}</span>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: u.activo ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          color: u.activo ? 'var(--accent-green)' : 'var(--accent-red)',
                        }}
                      >
                        {u.activo ? 'Activo' : 'Suspendido'}
                      </span>
                    </td>
                    <td style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {u.ultimoAcceso ? formatearFechaHora(u.ultimoAcceso) : 'Sin accesos'}
                    </td>
                    {esAdminOOwner && (
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => abrirEditar(u)}
                            title="Editar usuario"
                          >
                            <IconoEditar size={12} />
                            <span>Editar</span>
                          </button>
                          {usuarioActual?.id !== u.id && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => eliminarUsuario(u)}
                              style={{ color: 'var(--accent-red)' }}
                              title="Eliminar usuario definitivamente"
                              disabled={eliminarUsuarioMut.isPending}
                            >
                              <IconoBasura size={12} />
                              <span>Eliminar</span>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal Crear */}
      {modalCrearAbierto && (
        <div className="modal-overlay" onClick={() => setModalCrearAbierto(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registrar Nuevo Usuario en la Empresa</h2>
              <button type="button" className="btn-icono" onClick={() => setModalCrearAbierto(false)} aria-label="Cerrar">
                <IconoCerrar size={14} />
              </button>
            </div>
            <form onSubmit={guardarCrear}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label htmlFor="usr_nom">Nombre Completo *</label>
                  <input
                    id="usr_nom"
                    type="text"
                    required
                    placeholder="Ej: Camila Soto"
                    value={nuevoNombre}
                    onChange={(e) => setNuevoNombre(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="usr_email">Email Corporativo *</label>
                  <input
                    id="usr_email"
                    type="email"
                    required
                    placeholder="camila.soto@lexicore.cl"
                    value={nuevoEmail}
                    onChange={(e) => setNuevoEmail(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="usr_pass">Contraseña Inicial *</label>
                  <input
                    id="usr_pass"
                    type="password"
                    required
                    minLength={8}
                    placeholder="Mínimo 8 caracteres"
                    value={nuevoPassword}
                    onChange={(e) => setNuevoPassword(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="usr_rol">Rol & Nivel de Permisos</label>
                  <select
                    id="usr_rol"
                    value={nuevoRol}
                    onChange={(e) => setNuevoRol(e.target.value as Rol)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ETIQUETAS_ROL[r]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalCrearAbierto(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={crearMut.isPending}>
                  {crearMut.isPending ? 'Creando…' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {modalEditarAbierto && usuarioEditando && (
        <div className="modal-overlay" onClick={() => setModalEditarAbierto(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar Usuario: {usuarioEditando.email}</h2>
              <button type="button" className="btn-icono" onClick={() => setModalEditarAbierto(false)} aria-label="Cerrar">
                <IconoCerrar size={14} />
              </button>
            </div>
            <form onSubmit={guardarEditar}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label htmlFor="uedit_nom">Nombre Completo</label>
                  <input
                    id="uedit_nom"
                    type="text"
                    required
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="uedit_rol">Rol Asignado</label>
                  <select
                    id="uedit_rol"
                    value={editRol}
                    onChange={(e) => setEditRol(e.target.value as Rol)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ETIQUETAS_ROL[r]}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    id="uedit_act"
                    type="checkbox"
                    checked={editActivo}
                    onChange={(e) => setEditActivo(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <label htmlFor="uedit_act" style={{ fontSize: 13, cursor: 'pointer', margin: 0 }}>
                    Usuario Activo (Permite iniciar sesión en el sistema)
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalEditarAbierto(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actualizarMut.isPending}>
                  {actualizarMut.isPending ? 'Guardando…' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 7. GASTOS DE EMPRESA & RRHH (MÓDULO A: COGS / MÓDULO B: OPEX & NÓMINA)
// ═══════════════════════════════════════════════════════════════════════════════

const PERIODICIDADES = [
  { valor: 'MENSUAL', etiqueta: 'Mensual' },
  { valor: 'BIMESTRAL', etiqueta: 'Bimestral' },
  { valor: 'TRIMESTRAL', etiqueta: 'Trimestral' },
  { valor: 'SEMESTRAL', etiqueta: 'Semestral' },
  { valor: 'ANUAL', etiqueta: 'Anual' },
];

type SubPestanaGastosRrhh = 'nomina' | 'previred' | 'estructura' | 'servicios';

function PestanaGastosEmpresaRrhh() {
  const navegar = useNavigate();
  const avisos = useAvisos();

  // Sub-navegación interna
  const [subPestana, setSubPestana] = useState<SubPestanaGastosRrhh>('nomina');

  // Control temporal para Nómina y Previred (formato YYYY-MM)
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));

  // Modales de Nómina y RRHH (Módulo B)
  const [modalRegistrarAbierto, setModalRegistrarAbierto] = useState(false);
  const [remuneracionParaPagar, setRemuneracionParaPagar] = useState<any | null>(null);
  const [modalBonoAbierto, setModalBonoAbierto] = useState(false);
  const [modalPreviredAbierto, setModalPreviredAbierto] = useState(false);

  // Modales de Gastos Fijos de Estructura
  const [creando, setCreando] = useState(false);
  const [creandoRecurrente, setCreandoRecurrente] = useState(false);
  const [gastoParaPagar, setGastoParaPagar] = useState<any | null>(null);

  // Filtros de Nómina (100% reactivos)
  const [busquedaNomina, setBusquedaNomina] = useState('');
  const [filtroEstadoPagoNomina, setFiltroEstadoPagoNomina] = useState<'TODOS' | 'PENDIENTE' | 'PAGADO'>('TODOS');
  const [filtroPrevisionNomina, setFiltroPrevisionNomina] = useState<string>('TODAS');

  // Filtros de Gastos Fijos de Estructura
  const [busquedaGasto, setBusquedaGasto] = useState('');
  const [filtroEstadoPago, setFiltroEstadoPago] = useState<'TODOS' | 'PAGADO' | 'PENDIENTE'>('TODOS');
  const [filtroCategoriaId, setFiltroCategoriaId] = useState<string>('TODAS');
  const [filtroPeriodo, setFiltroPeriodo] = useState<'TODOS' | 'ESTE_MES' | 'TRES_MESES' | 'ESTE_ANO'>('TODOS');

  // Filtros de Costo de Servicios & Proyectos (COGS)
  const [busquedaServicio, setBusquedaServicio] = useState('');
  const [filtroAlertaServicio, setFiltroAlertaServicio] = useState<'TODOS' | 'SOBRECOSTO' | 'ALERTA' | 'NORMAL'>('TODOS');

  // Consultas Backend
  const consolaRrhhQuery = useConsolaRrhh(mes);
  const remuneracionesQuery = useRemuneraciones({
    periodoMes: mes,
    estadoPago: filtroEstadoPagoNomina !== 'TODOS' ? filtroEstadoPagoNomina : undefined,
    busqueda: busquedaNomina.trim() || undefined,
    prevision: filtroPrevisionNomina !== 'TODAS' ? filtroPrevisionNomina : undefined,
  });
  const planillaPreviredQuery = usePlanillaPrevired(mes);
  const consolaServiciosQuery = useConsolaCostosServicios();

  // Consultas de Gastos Fijos y Recurrentes
  const gastosQuery = useGastos({ soloGeneral: true, porPagina: 200 });
  const categoriasGastoQuery = useCategoriasGastoConfig(false);
  const recurrentes = useGastosRecurrentes();
  const eliminar = useEliminarGasto();
  const eliminarRecurrente = useEliminarGastoRecurrente();
  const generar = useGenerarRecurrentes();
  const cargarNominaMut = useCargarNominaMes();

  // Navegación mensual
  const cambiarMes = (delta: number) => {
    const [añoStr, mesStr] = mes.split('-');
    const fecha = new Date(Number(añoStr), Number(mesStr) - 1 + delta, 1);
    const nuevoAño = fecha.getFullYear();
    const nuevoMes = String(fecha.getMonth() + 1).padStart(2, '0');
    setMes(`${nuevoAño}-${nuevoMes}`);
  };

  const handleCargarNominaMes = async () => {
    if (!window.confirm(`¿Deseas generar la nómina de ${mes} clonando los colaboradores y sueldos base del mes anterior?`)) {
      return;
    }
    try {
      const res = await cargarNominaMut.mutateAsync({ periodoMes: mes, duplicarMesAnterior: true });
      avisos.exito(res.mensaje || `Se cargaron ${res.registrosCreados} remuneraciones para ${mes}.`);
      void consolaRrhhQuery.refetch();
      void remuneracionesQuery.refetch();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al cargar nómina mensual.');
    }
  };

  const ejecutarGeneracionRecurrentes = async () => {
    try {
      const resultado = await generar.mutateAsync();
      avisos.mostrar(resultado.mensaje, resultado.generados > 0 ? 'exito' : 'info');
      void gastosQuery.refetch();
      void recurrentes.refetch();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudieron generar los gastos recurrentes.');
    }
  };

  // Filtrado reactivo en memoria de Nómina
  const todasRemuneraciones = remuneracionesQuery.data?.datos ?? [];
  const remuneracionesFiltradas = todasRemuneraciones.filter((r) => {
    if (filtroPrevisionNomina !== 'TODAS' && !r.prevision.toUpperCase().includes(filtroPrevisionNomina.toUpperCase())) {
      return false;
    }
    return true;
  });

  // Filtrado reactivo de Gastos Generales de Estructura
  const todosGastos = gastosQuery.data?.datos ?? [];
  const totalNetoGlobal = todosGastos.reduce((suma, gasto) => suma + gasto.montoNeto, 0);
  const pendientesGlobal = todosGastos.filter((gasto) => gasto.estadoPago === 'PENDIENTE');
  const activos = recurrentes.data?.filter((plantilla) => plantilla.activo) ?? [];
  const mensualizado = activos.reduce((suma, plantilla) => {
    const meses: Record<string, number> = { MENSUAL: 1, BIMESTRAL: 2, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
    return suma + plantilla.montoNeto / (meses[plantilla.periodicidad] ?? 1);
  }, 0);
  const hoyStr = new Date().toISOString().slice(0, 10);
  const vencidosRecurrentes = activos.filter((plantilla) => plantilla.proximaFecha.slice(0, 10) <= hoyStr);

  const ahora = new Date();
  const anioActual = ahora.getFullYear();
  const mesActual = ahora.getMonth();
  const hace3Meses = new Date(ahora.getFullYear(), ahora.getMonth() - 3, 1);

  const gastosFiltrados = todosGastos.filter((gasto) => {
    if (filtroEstadoPago !== 'TODOS' && gasto.estadoPago !== filtroEstadoPago) return false;
    if (filtroCategoriaId !== 'TODAS' && gasto.categoriaGasto?.id !== filtroCategoriaId) return false;

    if (filtroPeriodo !== 'TODOS') {
      const f = new Date(gasto.fecha);
      if (filtroPeriodo === 'ESTE_MES') {
        if (f.getFullYear() !== anioActual || f.getMonth() !== mesActual) return false;
      } else if (filtroPeriodo === 'TRES_MESES') {
        if (f < hace3Meses) return false;
      } else if (filtroPeriodo === 'ESTE_ANO') {
        if (f.getFullYear() !== anioActual) return false;
      }
    }

    if (busquedaGasto.trim()) {
      const b = busquedaGasto.toLowerCase().trim();
      const concepto = (gasto.concepto ?? '').toLowerCase();
      const proveedor = (gasto.proveedorNombre ?? '').toLowerCase();
      const categoria = (gasto.categoriaGasto?.nombre ?? '').toLowerCase();
      if (!concepto.includes(b) && !proveedor.includes(b) && !categoria.includes(b)) return false;
    }

    return true;
  });

  const kpisRrhh = consolaRrhhQuery.data?.kpis;
  const kpisServicios = consolaServiciosQuery.data?.kpis;

  // Filtrado reactivo de Proyectos COGS
  const todosProyectosConsumo = consolaServiciosQuery.data?.proyectosConsumo ?? [];
  const proyectosConsumoFiltrados = todosProyectosConsumo.filter((p) => {
    if (filtroAlertaServicio === 'SOBRECOSTO' && p.estado !== 'EXCEDIDO') return false;
    if (filtroAlertaServicio === 'ALERTA' && p.estado !== 'ALERTA') return false;
    if (filtroAlertaServicio === 'NORMAL' && (p.estado === 'EXCEDIDO' || p.estado === 'ALERTA')) return false;
    if (busquedaServicio.trim()) {
      const b = busquedaServicio.toLowerCase().trim();
      const cod = (p.codigo ?? '').toLowerCase();
      const nom = (p.nombre ?? '').toLowerCase();
      const org = (p.organismoNombre ?? '').toLowerCase();
      if (!cod.includes(b) && !nom.includes(b) && !org.includes(b)) return false;
    }
    return true;
  });

  return (
    <>
      {/* ── SUB-NAVEGACIÓN INTERNA DE PESTAÑAS ── */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 18,
          padding: '6px',
          background: 'var(--bg-muted, rgba(255,255,255,0.03))',
          borderRadius: 8,
          border: '1px solid var(--border-color)',
        }}
      >
        <button
          type="button"
          onClick={() => setSubPestana('nomina')}
          className={`btn btn-sm ${subPestana === 'nomina' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <span>👥</span>
          <span>Nómina & Remuneraciones (Módulo B)</span>
        </button>

        <button
          type="button"
          onClick={() => setSubPestana('previred')}
          className={`btn btn-sm ${subPestana === 'previred' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <span>🏛️</span>
          <span>Leyes Sociales Previred (Día 13)</span>
        </button>

        <button
          type="button"
          onClick={() => setSubPestana('estructura')}
          className={`btn btn-sm ${subPestana === 'estructura' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <span>🏢</span>
          <span>Gastos Fijos de Estructura (Overhead)</span>
        </button>

        <button
          type="button"
          onClick={() => setSubPestana('servicios')}
          className={`btn btn-sm ${subPestana === 'servicios' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <span>📊</span>
          <span>Costo de Servicios & Proyectos (Módulo A - COGS)</span>
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════════
          SUB-PESTAÑA 1: NÓMINA & REMUNERACIONES (MÓDULO B - OPEX)
          ══════════════════════════════════════════════════════════════════════════ */}
      {subPestana === 'nomina' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Cabecera del Mes con Navegación y Acciones Rápidas */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              padding: '14px 18px',
              background: 'var(--bg-card)',
              borderRadius: 8,
              border: '1px solid var(--border-color)',
            }}
          >
            {/* Selector de Mes */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => cambiarMes(-1)}
                title="Mes Anterior"
              >
                ←
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Período:
                </span>
                <input
                  type="month"
                  value={mes}
                  onChange={(e) => setMes(e.target.value)}
                  style={{
                    padding: '4px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-muted)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => cambiarMes(1)}
                title="Mes Siguiente"
              >
                →
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setMes(new Date().toISOString().slice(0, 7))}
                style={{ fontSize: 11 }}
              >
                Hoy
              </button>
            </div>

            {/* Acciones 1-Clic de Nómina */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => void handleCargarNominaMes()}
                disabled={cargarNominaMut.isPending}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title="Clona la dotación del mes anterior estableciendo estado PENDIENTE"
              >
                <span>⚡</span>
                <span>{cargarNominaMut.isPending ? 'Cargando…' : 'Cargar Nómina Mes'}</span>
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setModalBonoAbierto(true)}
                disabled={todasRemuneraciones.length === 0}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span>🏆</span>
                <span>Asignar Bono</span>
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => window.open(`/api/rrhh/exportar.csv?mes=${mes}`, '_blank')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title="Descargar libro de remuneraciones en CSV para Excel / LRE"
              >
                <IconoDescargar size={13} />
                <span>Exportar CSV</span>
              </button>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setModalRegistrarAbierto(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <IconoMas size={13} />
                <span>Nueva Liquidación</span>
              </button>
            </div>
          </div>

          {/* Radar de 6 KPIs de Gastos de Empresa & RRHH */}
          <div className="kpi-grid">
            <TarjetaKpi
              etiqueta="Burn Rate Mensual Neto"
              valor={formatearCLP(kpisRrhh?.costoTotalFijoMesNeto ?? 0)}
              acento="var(--accent-red)"
              colorValor="var(--accent-red)"
              nota="Suma total de nómina + leyes sociales + oficina"
            />

            <TarjetaKpi
              etiqueta="Nómina Líquida Total"
              valor={formatearCLP(kpisRrhh?.nominaLiquidaTotal ?? 0)}
              acento="var(--accent-blue)"
              colorValor="var(--accent-blue)"
              nota={`${kpisRrhh?.colaboradoresActivosCount ?? todasRemuneraciones.length} colaboradores activos`}
            />

            <TarjetaKpi
              etiqueta="Previred & Leyes Sociales"
              valor={formatearCLP(kpisRrhh?.previredLeyesSocialesTotal ?? 0)}
              acento="var(--accent-purple)"
              nota="AFP, Salud Fonasa/Isapre, AFC, SIS y Mutual"
              onClick={() => setSubPestana('previred')}
            />

            <TarjetaKpi
              etiqueta="Semáforo Legal Previred"
              valor={
                kpisRrhh?.previredEstadoVencimiento === 'VENCIDO'
                  ? 'Vencido'
                  : kpisRrhh?.diasParaVencimientoPrevired !== undefined
                    ? `${kpisRrhh.diasParaVencimientoPrevired} días`
                    : 'Al Día'
              }
              acento={
                kpisRrhh?.previredEstadoVencimiento === 'VENCIDO'
                  ? 'var(--accent-red)'
                  : kpisRrhh?.previredEstadoVencimiento === 'POR_VENCER'
                    ? 'var(--accent-yellow)'
                    : 'var(--accent-green)'
              }
              colorValor={
                kpisRrhh?.previredEstadoVencimiento === 'VENCIDO'
                  ? 'var(--accent-red)'
                  : kpisRrhh?.previredEstadoVencimiento === 'POR_VENCER'
                    ? 'var(--accent-yellow)'
                    : 'var(--accent-green)'
              }
              nota={`Plazo legal: Día 13 (${kpisRrhh?.previredFechaVencimientoLegal ?? 'Próximo mes'})`}
              onClick={() => setModalPreviredAbierto(true)}
            />

            <TarjetaKpi
              etiqueta="Gastos Fijos de Oficina"
              valor={formatearCLP(kpisRrhh?.gastosFijosOficinaNeto ?? 0)}
              acento="var(--accent-gray)"
              nota="Arriendos, cloud y telecomunicaciones"
              onClick={() => setSubPestana('estructura')}
            />

            <TarjetaKpi
              etiqueta="Ratio Nómina / Ventas"
              valor={
                kpisRrhh?.ratioNominaVentasPct !== undefined
                  ? formatearPorcentajeSimple(kpisRrhh.ratioNominaVentasPct, 1)
                  : '—'
              }
              acento={kpisRrhh?.ratioNominaSaludable ? 'var(--accent-green)' : 'var(--accent-yellow)'}
              colorValor={kpisRrhh?.ratioNominaSaludable ? 'var(--accent-green)' : 'var(--accent-yellow)'}
              nota={
                kpisRrhh?.ratioNominaSaludable
                  ? '✅ En rango saludable (< 35%)'
                  : '⚠️ Precaución: ratio elevado'
              }
            />
          </div>

          {/* Panel de Tabla de Nómina con Filtros 100% Reactivos */}
          <Panel
            titulo={`Nómina Salarial del Período ${mes}`}
            subtitulo="Gestión de liquidaciones, sueldos base, bonos de licitación adjudicada y estado de pago"
          >
            {/* Barra de Filtros */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: 16,
                padding: '12px',
                background: 'var(--bg-muted, rgba(255,255,255,0.02))',
                borderRadius: 6,
                border: '1px solid var(--border-color)',
              }}
            >
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <input
                  type="text"
                  placeholder="🔍 Buscar por nombre, RUT o cargo…"
                  value={busquedaNomina}
                  onChange={(e) => setBusquedaNomina(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: 12,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>Estado:</span>
                <select
                  value={filtroEstadoPagoNomina}
                  onChange={(e) => setFiltroEstadoPagoNomina(e.target.value as any)}
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    borderRadius: 4,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <option value="TODOS">Todos los Estados</option>
                  <option value="PENDIENTE">⏳ Solo Pendientes</option>
                  <option value="PAGADO">✅ Solo Pagados</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>Previsión:</span>
                <select
                  value={filtroPrevisionNomina}
                  onChange={(e) => setFiltroPrevisionNomina(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    borderRadius: 4,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <option value="TODAS">Todas las Previsiones</option>
                  <option value="FONASA">FONASA</option>
                  <option value="HABITAT">AFP HABITAT</option>
                  <option value="CUPRUM">AFP CUPRUM</option>
                  <option value="PROVIDA">AFP PROVIDA</option>
                  <option value="MODELO">AFP MODELO</option>
                  <option value="ISAPRE">ISAPRES</option>
                </select>
              </div>

              {(busquedaNomina || filtroEstadoPagoNomina !== 'TODOS' || filtroPrevisionNomina !== 'TODAS') && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setBusquedaNomina('');
                    setFiltroEstadoPagoNomina('TODOS');
                    setFiltroPrevisionNomina('TODAS');
                  }}
                  style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <IconoRestablecer size={11} />
                  <span>Limpiar filtros</span>
                </button>
              )}
            </div>

            {/* Chips de filtros activos en Nómina */}
            {(busquedaNomina || filtroEstadoPagoNomina !== 'TODOS' || filtroPrevisionNomina !== 'TODAS') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros nómina:</span>
                {busquedaNomina.trim() && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: 'var(--accent-purple-soft)',
                      color: 'var(--accent-purple)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    <span>Búsqueda: <strong>"{busquedaNomina}"</strong></span>
                    <button
                      type="button"
                      onClick={() => setBusquedaNomina('')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de búsqueda"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                {filtroEstadoPagoNomina !== 'TODOS' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: filtroEstadoPagoNomina === 'PAGADO' ? 'var(--accent-green-soft)' : 'var(--accent-yellow-soft)',
                      color: filtroEstadoPagoNomina === 'PAGADO' ? 'var(--accent-green)' : 'var(--accent-yellow)',
                      border: `1px solid ${filtroEstadoPagoNomina === 'PAGADO' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                    }}
                  >
                    <span>Estado: <strong>{filtroEstadoPagoNomina === 'PAGADO' ? 'Pagado' : 'Pendiente'}</strong></span>
                    <button
                      type="button"
                      onClick={() => setFiltroEstadoPagoNomina('TODOS')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de estado de pago"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                {filtroPrevisionNomina !== 'TODAS' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: 'var(--accent-blue-soft)',
                      color: 'var(--accent-blue)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    <span>Previsión: <strong>{filtroPrevisionNomina}</strong></span>
                    <button
                      type="button"
                      onClick={() => setFiltroPrevisionNomina('TODAS')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de previsión"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
              </div>
            )}

            {remuneracionesQuery.isLoading && <Cargando texto="Cargando nómina salarial…" />}

            {remuneracionesQuery.isError && (
              <ErrorCarga
                error={remuneracionesQuery.error}
                reintentar={() => void remuneracionesQuery.refetch()}
              />
            )}

            {remuneracionesQuery.data && remuneracionesFiltradas.length === 0 && (
              <Vacio
                titulo="Sin colaboradores registrados en este período"
                detalle='Presiona "Cargar Nómina Mes" para duplicar la dotación del mes anterior o "Nueva Liquidación" para agregar colaboradores manualmente.'
              />
            )}

            {remuneracionesQuery.data && remuneracionesFiltradas.length > 0 && (
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Colaborador</th>
                      <th>Cargo & Previsión</th>
                      <th style={{ textAlign: 'right' }}>Sueldo Base</th>
                      <th style={{ textAlign: 'right' }}>Previsión</th>
                      <th style={{ textAlign: 'right' }}>Bonos Extra</th>
                      <th style={{ textAlign: 'right' }}>Sueldo Líquido</th>
                      <th style={{ textAlign: 'center' }}>Estado Pago</th>
                      <th style={{ textAlign: 'center' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {remuneracionesFiltradas.map((r) => {
                      const esPagado = r.estadoPago === 'PAGADO';
                      const tieneBonoLicitacion = !!r.licitacionBonoId || (r.detalleBonos && r.detalleBonos.toLowerCase().includes('licitaci'));

                      return (
                        <tr key={r.id}>
                          <td>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                              {r.nombreEmpleado}
                            </div>
                            {r.rutEmpleado && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                RUT: {r.rutEmpleado}
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ color: 'var(--text-secondary)' }}>{r.cargo}</div>
                            <span
                              style={{
                                display: 'inline-block',
                                fontSize: 10,
                                padding: '1px 6px',
                                borderRadius: 3,
                                background: 'var(--bg-muted)',
                                color: 'var(--text-secondary)',
                                marginTop: 2,
                              }}
                            >
                              {r.prevision}
                            </span>
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right' }}>
                            {formatearCLP(r.sueldoBase)}
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                            {formatearCLP(r.montoPrevision)}
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right' }}>
                            {r.bonos > 0 ? (
                              <div>
                                <span style={{ color: 'var(--accent-purple, #8b5cf6)', fontWeight: 600 }}>
                                  +{formatearCLP(r.bonos)}
                                </span>
                                {tieneBonoLicitacion && (
                                  <span
                                    style={{
                                      display: 'block',
                                      fontSize: 9.5,
                                      color: 'var(--accent-green)',
                                      marginTop: 1,
                                    }}
                                    title={r.detalleBonos ?? 'Bono por éxito en licitación'}
                                  >
                                    🏆 Licitación MP
                                  </span>
                                )}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td
                            className="td-mono"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: 'var(--text-primary)',
                              fontSize: 13,
                            }}
                          >
                            {formatearCLP(r.sueldoLiquido + (r.bonos || 0))}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '3px 8px',
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 600,
                                background: esPagado
                                  ? 'var(--accent-green-soft, rgba(16,185,129,0.15))'
                                  : 'var(--accent-yellow-soft, rgba(245,158,11,0.15))',
                                color: esPagado ? 'var(--accent-green, #10b981)' : 'var(--accent-yellow, #f59e0b)',
                              }}
                            >
                              <span>{esPagado ? '✅' : '⏳'}</span>
                              <span>{esPagado ? 'PAGADO' : 'PENDIENTE'}</span>
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {!esPagado ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setRemuneracionParaPagar(r)}
                                style={{
                                  fontSize: 11,
                                  padding: '3px 8px',
                                  color: 'var(--accent-green)',
                                  fontWeight: 600,
                                }}
                              >
                                💳 Pagar
                              </button>
                            ) : (
                              <span
                                style={{ fontSize: 10.5, color: 'var(--text-muted)' }}
                                title={r.notas ?? undefined}
                              >
                                {r.fechaPago ? formatearFechaCorta(r.fechaPago) : 'Pagado'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--bg-muted, rgba(255,255,255,0.02))', fontWeight: 700 }}>
                      <td colSpan={2}>
                        Totales ({remuneracionesFiltradas.length} colaboradores filtrados):
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right' }}>
                        {formatearCLP(remuneracionesFiltradas.reduce((s, r) => s + r.sueldoBase, 0))}
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right' }}>
                        {formatearCLP(remuneracionesFiltradas.reduce((s, r) => s + r.montoPrevision, 0))}
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right', color: 'var(--accent-purple)' }}>
                        {formatearCLP(remuneracionesFiltradas.reduce((s, r) => s + r.bonos, 0))}
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right', color: 'var(--accent-green)' }}>
                        {formatearCLP(
                          remuneracionesFiltradas.reduce((s, r) => s + r.sueldoLiquido + (r.bonos || 0), 0),
                        )}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          SUB-PESTAÑA 2: PLANILLA PREVIRED & LEYES SOCIALES (DÍA 13)
          ══════════════════════════════════════════════════════════════════════════ */}
      {subPestana === 'previred' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {planillaPreviredQuery.isLoading && <Cargando texto="Consultando leyes sociales en Previred…" />}

          {planillaPreviredQuery.isError && (
            <ErrorCarga
              error={planillaPreviredQuery.error}
              reintentar={() => void planillaPreviredQuery.refetch()}
            />
          )}

          {planillaPreviredQuery.data && (
            <>
              {/* Tarjeta de Cumplimiento Legal */}
              <div
                style={{
                  padding: '18px 22px',
                  borderRadius: 8,
                  background:
                    planillaPreviredQuery.data.estado === 'VENCIDO'
                      ? 'rgba(239, 68, 68, 0.1)'
                      : planillaPreviredQuery.data.estado === 'POR_VENCER'
                        ? 'rgba(245, 158, 11, 0.1)'
                        : 'rgba(16, 185, 129, 0.1)',
                  border:
                    planillaPreviredQuery.data.estado === 'VENCIDO'
                      ? '1px solid var(--accent-red, #ef4444)'
                      : planillaPreviredQuery.data.estado === 'POR_VENCER'
                        ? '1px solid var(--accent-yellow, #f59e0b)'
                        : '1px solid var(--accent-green, #10b981)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 16,
                }}
              >
                <div>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 4,
                      background:
                        planillaPreviredQuery.data.estado === 'VENCIDO'
                          ? 'var(--accent-red)'
                          : planillaPreviredQuery.data.estado === 'POR_VENCER'
                            ? 'var(--accent-yellow)'
                            : 'var(--accent-green)',
                      color: '#ffffff',
                      marginBottom: 6,
                    }}
                  >
                    {planillaPreviredQuery.data.estado === 'VENCIDO'
                      ? '🚨 VENCIMIENTO LEGAL EXPIRADO'
                      : planillaPreviredQuery.data.estado === 'POR_VENCER'
                        ? '⚠️ POR VENCER (< 5 DÍAS)'
                        : '✅ COTIZACIONES AL DÍA'}
                  </span>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Fecha Límite Legal Previred: {planillaPreviredQuery.data.fechaVencimientoLegal} (Día 13)
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {planillaPreviredQuery.data.diasRestantes > 0
                      ? `Faltan ${planillaPreviredQuery.data.diasRestantes} días para el pago sin recargos previsionales.`
                      : 'El plazo legal del día 13 ha expirado. Regulariza en Previred para evitar intereses.'}
                  </p>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Gran Total a Declarar</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>
                    {formatearCLP(planillaPreviredQuery.data.granTotalPrevired)}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {planillaPreviredQuery.data.totalTrabajadores} colaboradores cotizantes
                  </div>
                </div>
              </div>

              {/* 5 Tarjetas por Rama Previsional */}
              <div className="kpi-grid">
                <TarjetaKpi
                  etiqueta="Cotizaciones AFP"
                  valor={formatearCLP(planillaPreviredQuery.data.afpTotal)}
                  acento="var(--accent-blue)"
                  colorValor="var(--accent-blue)"
                  nota="Fondo de pensiones obligatorio"
                />

                <TarjetaKpi
                  etiqueta="Salud (Fonasa / Isapre)"
                  valor={formatearCLP(planillaPreviredQuery.data.saludTotal)}
                  acento="var(--accent-green)"
                  colorValor="var(--accent-green)"
                  nota="7% legal obligatorio de salud"
                />

                <TarjetaKpi
                  etiqueta="Mutualidad ACHS / CChC"
                  valor={formatearCLP(planillaPreviredQuery.data.mutualTotal)}
                  acento="var(--accent-purple)"
                  nota="Seguro laboral contra accidentes y faena"
                />

                <TarjetaKpi
                  etiqueta="Seguro de Cesantía (AFC)"
                  valor={formatearCLP(planillaPreviredQuery.data.afcTotal)}
                  acento="var(--accent-yellow)"
                  nota="Aporte obligatorio empleador/trabajador"
                />
              </div>

              {/* Tabla de Detalle por Institución */}
              <Panel
                titulo="Cuadratura Oficial para Portal Previred"
                subtitulo="Montos consolidados por entidad recaudadora para ingreso en el portal www.previred.com"
                acciones={
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => window.print()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <IconoDescargar size={13} />
                    <span>Imprimir / Exportar Cuadratura</span>
                  </button>
                }
              >
                <div className="tabla-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Tipo Institución</th>
                        <th>Institución Previsional</th>
                        <th style={{ textAlign: 'center' }}>N° Trabajadores</th>
                        <th style={{ textAlign: 'right' }}>Monto a Declarar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planillaPreviredQuery.data.detallePorInstitucion.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 600 }}>
                            <span
                              style={{
                                padding: '2px 6px',
                                fontSize: 10.5,
                                borderRadius: 3,
                                background: 'var(--bg-muted)',
                                color: 'var(--text-secondary)',
                              }}
                            >
                              {item.tipo}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {item.institucion}
                          </td>
                          <td style={{ textAlign: 'center' }}>{item.trabajadoresCount}</td>
                          <td className="td-mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                            {formatearCLP(item.montoTotal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          SUB-PESTAÑA 3: GASTOS FIJOS DE ESTRUCTURA (OVERHEAD)
          ══════════════════════════════════════════════════════════════════════════ */}
      {subPestana === 'estructura' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Banners de Sincronización */}
          <div
            style={{
              background: 'var(--accent-green-soft, rgba(16, 185, 129, 0.08))',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
              <span style={{ fontSize: 18 }}>📈</span>
              <div>
                <strong style={{ color: 'var(--text-primary)' }}>Impacto en Torre de Control & Resultado Operacional:</strong>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Los gastos fijos de estructura mensualizados se descuentan de la utilidad bruta para calcular el Resultado Operacional Real en el Dashboard General.
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => navegar('/')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
            >
              <span>Ver en Torre de Control</span>
              <IconoDerecha size={12} />
            </button>
          </div>

          <div className="kpi-grid">
            <TarjetaKpi
              etiqueta="Gastos Generales Históricos"
              valor={formatearCLP(totalNetoGlobal)}
              acento="var(--accent-purple)"
              nota={`${todosGastos.length} registros en total`}
            />
            <TarjetaKpi
              etiqueta="Pendientes de Pago"
              valor={formatearCLP(pendientesGlobal.reduce((suma, gasto) => suma + gasto.montoNeto, 0))}
              acento="var(--accent-yellow)"
              colorValor={pendientesGlobal.length > 0 ? 'var(--accent-yellow)' : undefined}
              nota={`${pendientesGlobal.length} sin pagar`}
            />
            <TarjetaKpi
              etiqueta="Costo Fijo Mensualizado"
              valor={formatearCLP(mensualizado)}
              acento="var(--accent-blue)"
              colorValor="var(--accent-blue)"
              nota={`${activos.length} plantilla(s) recurrente(s) activa(s)`}
            />
            <TarjetaKpi
              etiqueta="Recurrentes por Generar"
              valor={vencidosRecurrentes.length}
              acento={vencidosRecurrentes.length > 0 ? 'var(--accent-red)' : 'var(--accent-gray)'}
              colorValor={vencidosRecurrentes.length > 0 ? 'var(--accent-red)' : undefined}
              nota={vencidosRecurrentes.length > 0 ? 'Vencidas pendientes de emisión' : 'Todo al día'}
            />
          </div>

          {/* Panel de Gastos Recurrentes */}
          <Panel
            titulo="Gastos Recurrentes & Costos Fijos"
            subtitulo="Hosting, arriendos, software y servicios de estructura que se repiten periódicamente"
            acciones={
              <div style={{ display: 'flex', gap: 8 }}>
                {vencidosRecurrentes.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => void ejecutarGeneracionRecurrentes()}
                    disabled={generar.isPending}
                    style={{ color: 'var(--accent-red)' }}
                  >
                    {generar.isPending ? 'Generando…' : `Generar ${vencidosRecurrentes.length} pendiente(s)`}
                  </button>
                )}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreandoRecurrente(true)}>
                  <IconoMas size={13} />
                  <span>Nueva Plantilla Recurrente</span>
                </button>
              </div>
            }
          >
            {recurrentes.isLoading && <Cargando />}

            {recurrentes.data && recurrentes.data.length === 0 && (
              <Vacio
                titulo="Sin gastos recurrentes definidos"
                detalle="Una plantilla recurrente genera el gasto cada período sin que tengas que cargarlo manualmente cada mes."
              />
            )}

            {recurrentes.data && recurrentes.data.length > 0 && (
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Categoría</th>
                      <th style={{ textAlign: 'right' }}>Monto Neto</th>
                      <th>Periodicidad</th>
                      <th>Próxima Fecha</th>
                      <th>Estado</th>
                      <th style={{ textAlign: 'center' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recurrentes.data.map((plantilla) => (
                      <tr key={plantilla.id}>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {plantilla.descripcion}
                          </div>
                          {plantilla.proveedorNombre && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              Proveedor: {plantilla.proveedorNombre}
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11,
                              background: 'var(--surface-hover)',
                              color: 'var(--text-secondary)',
                              border: '1px solid var(--border)',
                            }}
                          >
                            {plantilla.categoriaGasto?.nombre ?? 'General'}
                          </span>
                        </td>
                        <td className="td-mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatearCLP(plantilla.montoNeto)}
                        </td>
                        <td>
                          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                            {PERIODICIDADES.find((p) => p.valor === plantilla.periodicidad)?.etiqueta ?? plantilla.periodicidad}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: 12,
                              color: plantilla.proximaFecha.slice(0, 10) <= hoyStr ? 'var(--accent-red)' : 'var(--text-secondary)',
                              fontWeight: plantilla.proximaFecha.slice(0, 10) <= hoyStr ? 700 : 400,
                            }}
                          >
                            {formatearFechaCorta(plantilla.proximaFecha)}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              fontSize: 11,
                              borderRadius: 4,
                              background: plantilla.activo ? 'var(--accent-green-soft)' : 'var(--bg-muted)',
                              color: plantilla.activo ? 'var(--accent-green)' : 'var(--text-muted)',
                            }}
                          >
                            {plantilla.activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent-red)' }}
                            onClick={() => {
                              if (window.confirm(`¿Eliminar plantilla recurrente "${plantilla.descripcion}"?`)) {
                                void eliminarRecurrente.mutateAsync(plantilla.id);
                              }
                            }}
                            title="Eliminar plantilla"
                          >
                            <IconoBasura size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {/* Panel de Libro de Gastos Generales */}
          <Panel
            titulo="Libro de Gastos Generales Registrados"
            subtitulo="Historial de egresos y facturas de compras de estructura"
            acciones={
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>
                <IconoMas size={13} />
                <span>Nuevo Gasto General</span>
              </button>
            }
          >
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: 16,
                padding: '12px',
                background: 'var(--bg-muted, rgba(255,255,255,0.02))',
                borderRadius: 6,
                border: '1px solid var(--border-color)',
              }}
            >
              <div style={{ flex: '1 1 200px' }}>
                <input
                  type="text"
                  placeholder="🔍 Buscar por descripción o proveedor…"
                  value={busquedaGasto}
                  onChange={(e) => setBusquedaGasto(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: 12,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                  }}
                />
              </div>

              <select
                value={filtroEstadoPago}
                onChange={(e) => setFiltroEstadoPago(e.target.value as any)}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  borderRadius: 4,
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <option value="TODOS">Todos los Estados</option>
                <option value="PENDIENTE">⏳ Solo Pendientes</option>
                <option value="PAGADO">✅ Solo Pagados</option>
              </select>

              <select
                value={filtroCategoriaId}
                onChange={(e) => setFiltroCategoriaId(e.target.value)}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  borderRadius: 4,
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <option value="TODAS">Todas las Categorías</option>
                {categoriasGastoQuery.data?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </select>

              <select
                value={filtroPeriodo}
                onChange={(e) => setFiltroPeriodo(e.target.value as any)}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  borderRadius: 4,
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <option value="TODOS">Todos los Períodos</option>
                <option value="ESTE_MES">Este Mes</option>
                <option value="TRES_MESES">Últimos 3 Meses</option>
                <option value="ESTE_ANO">Año en Curso</option>
              </select>

              {(busquedaGasto || filtroEstadoPago !== 'TODOS' || filtroCategoriaId !== 'TODAS' || filtroPeriodo !== 'TODOS') && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setBusquedaGasto('');
                    setFiltroEstadoPago('TODOS');
                    setFiltroCategoriaId('TODAS');
                    setFiltroPeriodo('TODOS');
                  }}
                  style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <IconoRestablecer size={11} />
                  <span>Limpiar filtros</span>
                </button>
              )}
            </div>

            {/* Chips de filtros activos en Gastos Generales */}
            {(busquedaGasto || filtroEstadoPago !== 'TODOS' || filtroCategoriaId !== 'TODAS' || filtroPeriodo !== 'TODOS') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros activos:</span>
                {busquedaGasto.trim() && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: 'var(--accent-purple-soft)',
                      color: 'var(--accent-purple)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    <span>Búsqueda: <strong>"{busquedaGasto}"</strong></span>
                    <button
                      type="button"
                      onClick={() => setBusquedaGasto('')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de búsqueda"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                {filtroEstadoPago !== 'TODOS' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: filtroEstadoPago === 'PAGADO' ? 'var(--accent-green-soft)' : 'var(--accent-yellow-soft)',
                      color: filtroEstadoPago === 'PAGADO' ? 'var(--accent-green)' : 'var(--accent-yellow)',
                      border: `1px solid ${filtroEstadoPago === 'PAGADO' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                    }}
                  >
                    <span>Estado: <strong>{filtroEstadoPago === 'PAGADO' ? 'Pagado' : 'Pendiente'}</strong></span>
                    <button
                      type="button"
                      onClick={() => setFiltroEstadoPago('TODOS')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de estado"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                {filtroCategoriaId !== 'TODAS' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: 'var(--accent-blue-soft)',
                      color: 'var(--accent-blue)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    <span>Categoría: <strong>{categoriasGastoQuery.data?.find((c) => c.id === filtroCategoriaId)?.nombre ?? 'Categoría'}</strong></span>
                    <button
                      type="button"
                      onClick={() => setFiltroCategoriaId('TODAS')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de categoría"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                {filtroPeriodo !== 'TODOS' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: 'var(--accent-yellow-soft)',
                      color: 'var(--accent-yellow)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                    }}
                  >
                    <span>
                      Período: <strong>{filtroPeriodo === 'ESTE_MES' ? 'Este Mes' : filtroPeriodo === 'TRES_MESES' ? 'Últimos 3 Meses' : 'Año en Curso'}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiltroPeriodo('TODOS')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de período"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
              </div>
            )}

            {gastosFiltrados.length === 0 ? (
              <Vacio titulo="Sin gastos para los filtros aplicados" />
            ) : (
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Descripción / Concepto</th>
                      <th>Categoría</th>
                      <th style={{ textAlign: 'right' }}>Monto Neto</th>
                      <th style={{ textAlign: 'center' }}>Estado Pago</th>
                      <th style={{ textAlign: 'center' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gastosFiltrados.map((gasto) => (
                      <tr key={gasto.id}>
                        <td style={{ fontSize: 12 }}>{formatearFechaCorta(gasto.fecha)}</td>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{gasto.concepto}</div>
                          {gasto.proveedorNombre && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{gasto.proveedorNombre}</div>
                          )}
                        </td>
                        <td>
                          <span className="badge-categoria">{gasto.categoriaGasto?.nombre}</span>
                        </td>
                        <td className="td-mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatearCLP(gasto.montoNeto)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              fontSize: 11,
                              borderRadius: 4,
                              background: gasto.estadoPago === 'PAGADO' ? 'var(--accent-green-soft)' : 'var(--accent-yellow-soft)',
                              color: gasto.estadoPago === 'PAGADO' ? 'var(--accent-green)' : 'var(--accent-yellow)',
                            }}
                          >
                            {gasto.estadoPago}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            {gasto.estadoPago === 'PENDIENTE' && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setGastoParaPagar(gasto)}
                                style={{ fontSize: 10, padding: '2px 6px' }}
                              >
                                Pagar
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent-red)' }}
                              onClick={() => {
                                if (window.confirm(`¿Eliminar gasto "${gasto.concepto}"?`)) {
                                  void eliminar.mutateAsync(gasto.id);
                                }
                              }}
                            >
                              <IconoCerrar size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          SUB-PESTAÑA 4: COSTO DE SERVICIOS & PROYECTOS (MÓDULO A - COGS)
          ══════════════════════════════════════════════════════════════════════════ */}
      {subPestana === 'servicios' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Cabecera con Botón de Exportar y Enlace a Costos */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              padding: '14px 18px',
              background: 'var(--bg-card)',
              borderRadius: 8,
              border: '1px solid var(--border-color)',
            }}
          >
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                📊 Consola de Costo de Servicios & Proyectos (COGS)
              </h3>
              <p style={{ fontSize: 12, margin: '2px 0 0', color: 'var(--text-secondary)' }}>
                Auditoría de costos operacionales directos: materiales, subcontratos, viáticos y combustible en contratos adjudicados.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => window.open('/api/gastos/servicios/exportar.csv', '_blank')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title="Descargar auditoría de costos de servicios en CSV para Excel"
              >
                <IconoDescargar size={13} />
                <span>Exportar Auditoría CSV</span>
              </button>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navegar('/costos')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span>🚀 Ir a Radar de Costos</span>
              </button>
            </div>
          </div>

          {/* 5 KPIs de Costos Directos */}
          <div className="kpi-grid">
            <TarjetaKpi
              etiqueta="Presupuesto Total Servicios"
              valor={formatearCLP(kpisServicios?.presupuestoTotalServiciosNeto ?? 0)}
              acento="var(--accent-blue)"
              colorValor="var(--accent-blue)"
              nota={`${kpisServicios?.proyectosTotalCount ?? 0} proyectos evaluados`}
            />

            <TarjetaKpi
              etiqueta="Costo Directo Real Acumulado"
              valor={formatearCLP(kpisServicios?.costoDirectoRealAcumuladoNeto ?? 0)}
              acento="var(--accent-purple)"
              nota="Gasto real en faenas y contratos"
            />

            <TarjetaKpi
              etiqueta="Consumo Presupuestario Global"
              valor={
                kpisServicios?.porcentajeConsumoGlobalPct !== undefined
                  ? formatearPorcentajeSimple(kpisServicios.porcentajeConsumoGlobalPct, 1)
                  : '—'
              }
              acento={
                (kpisServicios?.porcentajeConsumoGlobalPct ?? 0) > 100
                  ? 'var(--accent-red)'
                  : (kpisServicios?.porcentajeConsumoGlobalPct ?? 0) > 85
                    ? 'var(--accent-yellow)'
                    : 'var(--accent-green)'
              }
              colorValor={
                (kpisServicios?.porcentajeConsumoGlobalPct ?? 0) > 100
                  ? 'var(--accent-red)'
                  : (kpisServicios?.porcentajeConsumoGlobalPct ?? 0) > 85
                    ? 'var(--accent-yellow)'
                    : 'var(--accent-green)'
              }
              nota="Porcentaje ejecutado de la cartera"
            />

            <TarjetaKpi
              etiqueta="Desviación Operacional Neta"
              valor={formatearCLP(kpisServicios?.desviacionOperacionalNeta ?? 0)}
              acento={(kpisServicios?.desviacionOperacionalNeta ?? 0) < 0 ? 'var(--accent-red)' : 'var(--accent-green)'}
              colorValor={(kpisServicios?.desviacionOperacionalNeta ?? 0) < 0 ? 'var(--accent-red)' : 'var(--accent-green)'}
              nota={
                (kpisServicios?.desviacionOperacionalNeta ?? 0) < 0
                  ? '🚨 Sobrecosto acumulado en faena'
                  : '✅ Ahorro respecto al presupuesto'
              }
            />

            <TarjetaKpi
              etiqueta="Proyectos en Alerta / Riesgo"
              valor={`${kpisServicios?.proyectosEnRiesgoCount ?? 0} proyectos`}
              acento={(kpisServicios?.proyectosEnRiesgoCount ?? 0) > 0 ? 'var(--accent-red)' : 'var(--accent-gray)'}
              colorValor={(kpisServicios?.proyectosEnRiesgoCount ?? 0) > 0 ? 'var(--accent-red)' : undefined}
              nota={`${kpisServicios?.proyectosExcedidosCount ?? 0} con sobrecosto crítico`}
            />
          </div>

          {/* Matriz Comparativa Plan vs Real por Categoría */}
          <Panel
            titulo="Matriz Comparativa Plan vs. Real por Categoría de Faena"
            subtitulo="Control presupuestario de materiales, subcontratación de terceros, viáticos de terreno y logística"
          >
            {consolaServiciosQuery.isLoading && <Cargando texto="Calculando costos directos de servicios…" />}

            {consolaServiciosQuery.isError && (
              <ErrorCarga
                error={consolaServiciosQuery.error}
                reintentar={() => void consolaServiciosQuery.refetch()}
              />
            )}

            {consolaServiciosQuery.data && (
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Categoría Operacional</th>
                      <th style={{ textAlign: 'right' }}>Presupuesto Planificado</th>
                      <th style={{ textAlign: 'right' }}>Gasto Real Ejecutado</th>
                      <th style={{ textAlign: 'right' }}>Desviación ($)</th>
                      <th style={{ textAlign: 'center' }}>% Consumo</th>
                      <th style={{ textAlign: 'center' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(consolaServiciosQuery.data.comparativoCategorias ?? []).map((cat) => {
                      const esExcedido = cat.estado === 'EXCEDIDO';
                      const esAlerta = cat.estado === 'ALERTA';

                      return (
                        <tr key={cat.categoriaId}>
                          <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {cat.nombre}
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right' }}>
                            {formatearCLP(cat.montoPlanificadoNeto)}
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                            {formatearCLP(cat.montoRealNeto)}
                          </td>
                          <td
                            className="td-mono"
                            style={{
                              textAlign: 'right',
                              color: cat.desviacionNeto > 0 ? 'var(--accent-red)' : 'var(--accent-green)',
                              fontWeight: 600,
                            }}
                          >
                            {cat.desviacionNeto > 0 ? `+${formatearCLP(cat.desviacionNeto)}` : formatearCLP(cat.desviacionNeto)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                              <div
                                style={{
                                  width: 60,
                                  height: 6,
                                  background: 'var(--border-color)',
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${Math.min(cat.porcentajeConsumoPct, 100)}%`,
                                    height: '100%',
                                    background: esExcedido
                                      ? 'var(--accent-red)'
                                      : esAlerta
                                        ? 'var(--accent-yellow)'
                                        : 'var(--accent-green)',
                                  }}
                                />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 600 }}>
                                {formatearPorcentajeSimple(cat.porcentajeConsumoPct, 1)}
                              </span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                fontSize: 10.5,
                                borderRadius: 4,
                                fontWeight: 700,
                                background: esExcedido
                                  ? 'var(--accent-red-soft, rgba(239,68,68,0.15))'
                                  : esAlerta
                                    ? 'var(--accent-yellow-soft, rgba(245,158,11,0.15))'
                                    : 'var(--accent-green-soft, rgba(16,185,129,0.15))',
                                color: esExcedido
                                  ? 'var(--accent-red)'
                                  : esAlerta
                                    ? 'var(--accent-yellow)'
                                    : 'var(--accent-green)',
                              }}
                            >
                              {esExcedido ? '🚨 EXCEDIDO' : esAlerta ? '⚠️ ALERTA' : '✅ ÓPTIMO'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {/* Ranking de Proyectos y Consumo Presupuestario */}
          <Panel
            titulo="Ranking de Proyectos por Consumo de Presupuesto Directo"
            subtitulo="Detección temprana de faenas con sobrecosto o en riesgo de déficit financiero"
          >
            {/* Barra de Filtros para Proyectos COGS */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: 16,
                padding: '12px',
                background: 'var(--bg-muted, rgba(255,255,255,0.02))',
                borderRadius: 6,
                border: '1px solid var(--border-color)',
              }}
            >
              <div style={{ flex: '1 1 240px', position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }}>
                  <IconoBuscar size={13} />
                </span>
                <input
                  type="text"
                  placeholder="Buscar por código, nombre de proyecto u organismo…"
                  value={busquedaServicio}
                  onChange={(e) => setBusquedaServicio(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 30px 6px 28px',
                    fontSize: 12,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                  }}
                />
                {busquedaServicio && (
                  <button
                    type="button"
                    onClick={() => setBusquedaServicio('')}
                    style={{
                      position: 'absolute',
                      right: 6,
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: 2,
                    }}
                    title="Limpiar búsqueda"
                  >
                    <IconoCerrar size={11} />
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>Semáforo de Alerta:</span>
                <select
                  value={filtroAlertaServicio}
                  onChange={(e) => setFiltroAlertaServicio(e.target.value as any)}
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    borderRadius: 4,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <option value="TODOS">Todos los Proyectos</option>
                  <option value="SOBRECOSTO">🚨 Solo Sobrecosto (Críticos)</option>
                  <option value="ALERTA">⚠️ En Alerta (&gt; 85%)</option>
                  <option value="NORMAL">✅ Normales (En Rango)</option>
                </select>
              </div>

              {(busquedaServicio || filtroAlertaServicio !== 'TODOS') && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setBusquedaServicio('');
                    setFiltroAlertaServicio('TODOS');
                  }}
                  style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <IconoRestablecer size={11} />
                  <span>Limpiar filtros</span>
                </button>
              )}
            </div>

            {/* Chips de filtros activos en Proyectos COGS */}
            {(busquedaServicio || filtroAlertaServicio !== 'TODOS') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros proyectos:</span>
                {busquedaServicio.trim() && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background: 'var(--accent-purple-soft)',
                      color: 'var(--accent-purple)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    <span>Búsqueda: <strong>"{busquedaServicio}"</strong></span>
                    <button
                      type="button"
                      onClick={() => setBusquedaServicio('')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de búsqueda"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                {filtroAlertaServicio !== 'TODOS' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11.5,
                      background:
                        filtroAlertaServicio === 'SOBRECOSTO'
                          ? 'var(--accent-red-soft)'
                          : filtroAlertaServicio === 'ALERTA'
                            ? 'var(--accent-yellow-soft)'
                            : 'var(--accent-green-soft)',
                      color:
                        filtroAlertaServicio === 'SOBRECOSTO'
                          ? 'var(--accent-red)'
                          : filtroAlertaServicio === 'ALERTA'
                            ? 'var(--accent-yellow)'
                            : 'var(--accent-green)',
                      border: `1px solid ${
                        filtroAlertaServicio === 'SOBRECOSTO'
                          ? 'rgba(239, 68, 68, 0.3)'
                          : filtroAlertaServicio === 'ALERTA'
                            ? 'rgba(245, 158, 11, 0.3)'
                            : 'rgba(16, 185, 129, 0.3)'
                      }`,
                    }}
                  >
                    <span>
                      Alerta: <strong>{filtroAlertaServicio === 'SOBRECOSTO' ? 'Sobrecosto' : filtroAlertaServicio === 'ALERTA' ? 'En Alerta' : 'Normal'}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiltroAlertaServicio('TODOS')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                      title="Quitar filtro de alerta"
                    >
                      <IconoCerrar size={11} />
                    </button>
                  </span>
                )}
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  Mostrando <strong>{proyectosConsumoFiltrados.length}</strong> de {todosProyectosConsumo.length} proyectos
                </div>
              </div>
            )}

            {consolaServiciosQuery.data && proyectosConsumoFiltrados.length === 0 && (
              <Vacio
                titulo="Sin proyectos para los filtros aplicados"
                detalle="Prueba con otro término de búsqueda o selecciona 'Todos los Proyectos' en el semáforo de alerta."
                accion={
                  (busquedaServicio || filtroAlertaServicio !== 'TODOS') ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setBusquedaServicio('');
                        setFiltroAlertaServicio('TODOS');
                      }}
                    >
                      Restablecer filtros
                    </button>
                  ) : undefined
                }
              />
            )}

            {consolaServiciosQuery.data && proyectosConsumoFiltrados.length > 0 && (
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Código & Proyecto</th>
                      <th>Cliente / Organismo</th>
                      <th style={{ textAlign: 'right' }}>Presupuesto Directo</th>
                      <th style={{ textAlign: 'right' }}>Costo Real</th>
                      <th style={{ textAlign: 'right' }}>Saldo Disponible</th>
                      <th style={{ textAlign: 'center' }}>% Consumo</th>
                      <th style={{ textAlign: 'center' }}>Alerta</th>
                      <th style={{ textAlign: 'center' }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proyectosConsumoFiltrados.map((p) => {
                      const esCritico = p.estado === 'EXCEDIDO';
                      const esAlerta = p.estado === 'ALERTA';

                      return (
                        <tr key={p.id}>
                          <td>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                              {p.codigo} — {p.nombre}
                            </div>
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                            {p.organismoNombre ?? 'Cliente Privado'}
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right' }}>
                            {formatearCLP(p.presupuestoNeto)}
                          </td>
                          <td className="td-mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                            {formatearCLP(p.costoRealNeto)}
                          </td>
                          <td
                            className="td-mono"
                            style={{
                              textAlign: 'right',
                              color: p.saldoDisponibleNeto < 0 ? 'var(--accent-red)' : 'var(--text-primary)',
                              fontWeight: p.saldoDisponibleNeto < 0 ? 700 : 400,
                            }}
                          >
                            {formatearCLP(p.saldoDisponibleNeto)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: esCritico ? 'var(--accent-red)' : esAlerta ? 'var(--accent-yellow)' : 'var(--accent-green)',
                              }}
                            >
                              {formatearPorcentajeSimple(p.porcentajeConsumoPct, 1)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                fontSize: 10.5,
                                borderRadius: 4,
                                fontWeight: 700,
                                background: esCritico
                                  ? 'var(--accent-red-soft, rgba(239,68,68,0.15))'
                                  : esAlerta
                                    ? 'var(--accent-yellow-soft, rgba(245,158,11,0.15))'
                                    : 'var(--accent-green-soft, rgba(16,185,129,0.15))',
                                color: esCritico
                                  ? 'var(--accent-red)'
                                  : esAlerta
                                    ? 'var(--accent-yellow)'
                                    : 'var(--accent-green)',
                              }}
                            >
                              {esCritico ? '🚨 SOBRECOSTO' : esAlerta ? '⚠️ > 85%' : '✅ NORMAL'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => navegar(`/proyectos/${p.id}`)}
                              style={{ fontSize: 10.5, padding: '2px 8px' }}
                            >
                              Ver Ficha →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ── MODALES DE NÓMINA Y RRHH (MÓDULO B) ── */}
      <ModalRegistrarRemuneracion
        abierto={modalRegistrarAbierto}
        alCerrar={() => {
          setModalRegistrarAbierto(false);
          void consolaRrhhQuery.refetch();
          void remuneracionesQuery.refetch();
        }}
        periodoMes={mes}
      />

      <ModalPagarRemuneracion
        remuneracion={remuneracionParaPagar}
        alCerrar={() => {
          setRemuneracionParaPagar(null);
          void consolaRrhhQuery.refetch();
          void remuneracionesQuery.refetch();
        }}
      />

      <ModalAsignarBonoLicitacion
        abierto={modalBonoAbierto}
        alCerrar={() => {
          setModalBonoAbierto(false);
          void consolaRrhhQuery.refetch();
          void remuneracionesQuery.refetch();
        }}
        periodoMes={mes}
        remuneraciones={todasRemuneraciones}
      />

      <ModalDetallePrevired
        abierto={modalPreviredAbierto}
        alCerrar={() => setModalPreviredAbierto(false)}
        mes={mes}
      />

      {/* ── MODALES DE GASTOS GENERALES Y RECURRENTES ── */}
      {creando && <ModalNuevoGastoEmpresa alCerrar={() => setCreando(false)} />}
      {creandoRecurrente && <ModalNuevaPlantilla alCerrar={() => setCreandoRecurrente(false)} />}
      {gastoParaPagar && (
        <ModalPagarGasto
          gasto={gastoParaPagar}
          alCerrar={() => setGastoParaPagar(null)}
          alPagarExitoso={() => void gastosQuery.refetch()}
        />
      )}
    </>
  );
}

const PestanaGastosGenerales = PestanaGastosEmpresaRrhh;


function ModalNuevoGastoEmpresa({ alCerrar }: { alCerrar: () => void }) {
  const avisos = useAvisos();
  const categorias = useCategoriasGastoConfig();
  const crear = useCrearGastoEnProyecto();

  const [descripcion, setDescripcion] = useState('');
  const [categoriaGastoId, setCategoriaGastoId] = useState('');
  const [montoNeto, setMontoNeto] = useState<number | ''>('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [proveedorNombre, setProveedorNombre] = useState('');
  const [estadoPago, setEstadoPago] = useState<'PENDIENTE' | 'PAGADO'>('PAGADO');

  useEffect(() => {
    if (categorias.data && categorias.data.length > 0 && !categoriaGastoId) {
      const indirecta = categorias.data.find((c) => c.tipo === 'INDIRECTO') ?? categorias.data[0];
      if (indirecta) setCategoriaGastoId(indirecta.id);
    }
  }, [categorias.data, categoriaGastoId]);

  const guardar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!descripcion.trim() || !categoriaGastoId || typeof montoNeto !== 'number' || montoNeto <= 0) {
      avisos.error('Completa los campos obligatorios con valores válidos.');
      return;
    }

    try {
      await crear.mutateAsync({
        proyectoId: null,
        categoriaGastoId,
        descripcion: descripcion.trim(),
        montoNeto,
        fecha,
        proveedorNombre: proveedorNombre.trim() || undefined,
        estadoPago,
      });
      avisos.exito('Gasto general registrado correctamente.');
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo guardar el gasto.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nuevo Gasto General</h2>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar">
            <IconoCerrar size={14} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label htmlFor="gasto_desc">Descripción o Concepto *</label>
              <input
                id="gasto_desc"
                type="text"
                required
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej: Servidor de producción AWS — Septiembre"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="gasto_cat">Categoría de Costo *</label>
                <select
                  id="gasto_cat"
                  required
                  value={categoriaGastoId}
                  onChange={(e) => setCategoriaGastoId(e.target.value)}
                >
                  {categorias.data?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre} ({cat.tipo})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="gasto_monto">Monto Neto (CLP) *</label>
                <input
                  id="gasto_monto"
                  type="number"
                  required
                  min={1}
                  value={montoNeto}
                  onChange={(e) => setMontoNeto(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="gasto_fecha">Fecha *</label>
                <input id="gasto_fecha" type="date" required value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </div>

              <div className="form-group">
                <label htmlFor="gasto_estado">Estado de Pago</label>
                <select
                  id="gasto_estado"
                  value={estadoPago}
                  onChange={(e) => setEstadoPago(e.target.value as 'PENDIENTE' | 'PAGADO')}
                >
                  <option value="PAGADO">Pagado</option>
                  <option value="PENDIENTE">Pendiente</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="gasto_prov">Proveedor (Opcional)</label>
              <input
                id="gasto_prov"
                type="text"
                value={proveedorNombre}
                onChange={(e) => setProveedorNombre(e.target.value)}
                placeholder="Amazon Web Services Inc."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={crear.isPending}>
              {crear.isPending ? 'Guardando…' : 'Guardar Gasto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalNuevaPlantilla({ alCerrar }: { alCerrar: () => void }) {
  const avisos = useAvisos();
  const categorias = useCategoriasGastoConfig();
  const crear = useCrearGastoRecurrente();

  const [descripcion, setDescripcion] = useState('');
  const [categoriaGastoId, setCategoriaGastoId] = useState('');
  const [montoNeto, setMontoNeto] = useState<number | ''>('');
  const [periodicidad, setPeriodicidad] = useState('MENSUAL');
  const [proximaFecha, setProximaFecha] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (categorias.data && categorias.data.length > 0 && !categoriaGastoId) {
      const indirecta = categorias.data.find((c) => c.tipo === 'INDIRECTO') ?? categorias.data[0];
      if (indirecta) setCategoriaGastoId(indirecta.id);
    }
  }, [categorias.data, categoriaGastoId]);

  const guardar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!descripcion.trim() || !categoriaGastoId || typeof montoNeto !== 'number' || montoNeto <= 0) {
      avisos.error('Completa los campos obligatorios.');
      return;
    }

    try {
      await crear.mutateAsync({
        categoriaGastoId,
        descripcion: descripcion.trim(),
        montoNeto,
        periodicidad,
        proximaFecha,
      });
      avisos.exito('Plantilla recurrente creada exitosamente.');
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo crear la plantilla.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nueva Plantilla de Gasto Recurrente</h2>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar">
            <IconoCerrar size={14} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label htmlFor="rec_desc">Descripción *</label>
              <input
                id="rec_desc"
                type="text"
                required
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej: Licencias Google Workspace"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="rec_cat">Categoría *</label>
                <select
                  id="rec_cat"
                  required
                  value={categoriaGastoId}
                  onChange={(e) => setCategoriaGastoId(e.target.value)}
                >
                  {categorias.data?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="rec_monto">Monto Neto (CLP) *</label>
                <input
                  id="rec_monto"
                  type="number"
                  required
                  min={1}
                  value={montoNeto}
                  onChange={(e) => setMontoNeto(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="rec_per">Periodicidad</label>
                <select id="rec_per" value={periodicidad} onChange={(e) => setPeriodicidad(e.target.value)}>
                  {PERIODICIDADES.map((p) => (
                    <option key={p.valor} value={p.valor}>
                      {p.etiqueta}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="rec_prox">Primera Fecha de Cobro *</label>
                <input
                  id="rec_prox"
                  type="date"
                  required
                  value={proximaFecha}
                  onChange={(e) => setProximaFecha(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={crear.isPending}>
              {crear.isPending ? 'Guardando…' : 'Guardar Plantilla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 8. HISTORIAL INMUTABLE DE AUDITORÍA (CON FILTROS 100%)
// ═══════════════════════════════════════════════════════════════════════════════

function PestanaAuditoriaConfiguracion() {
  const [limite, setLimite] = useState<number>(50);
  const [busqueda, setBusqueda] = useState('');
  const [filtroAccion, setFiltroAccion] = useState<'TODAS' | 'CREAR' | 'ACTUALIZAR' | 'ELIMINAR'>('TODAS');
  const [filtroEntidad, setFiltroEntidad] = useState<string>('TODAS');

  const auditoriaQuery = useAuditoriaConfiguracion(limite);

  if (auditoriaQuery.isLoading) return <Cargando texto="Cargando registro de auditoría…" />;
  if (auditoriaQuery.isError) return <ErrorCarga error={auditoriaQuery.error} reintentar={() => void auditoriaQuery.refetch()} />;

  const todosRegistros = auditoriaQuery.data ?? [];

  // Filtrado interactivo
  const registrosFiltrados = todosRegistros.filter((item) => {
    if (filtroAccion !== 'TODAS' && item.accion !== filtroAccion) return false;
    if (filtroEntidad !== 'TODAS' && item.entidad !== filtroEntidad) return false;

    if (busqueda.trim()) {
      const b = busqueda.toLowerCase().trim();
      const usuario = (item.usuarioNombre ?? '').toLowerCase();
      const email = (item.usuarioEmail ?? '').toLowerCase();
      const cambios = (item.cambios ?? '').toLowerCase();
      const entidad = (ETIQUETAS_ENTIDAD[item.entidad] ?? item.entidad).toLowerCase();
      if (!usuario.includes(b) && !email.includes(b) && !cambios.includes(b) && !entidad.includes(b)) {
        return false;
      }
    }

    return true;
  });

  const totalCreaciones = todosRegistros.filter((r) => r.accion === 'CREAR').length;
  const totalActualizaciones = todosRegistros.filter((r) => r.accion === 'ACTUALIZAR').length;
  const totalEliminaciones = todosRegistros.filter((r) => r.accion === 'ELIMINAR').length;

  const hayFiltrosActivos = busqueda.trim() !== '' || filtroAccion !== 'TODAS' || filtroEntidad !== 'TODAS';

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroAccion('TODAS');
    setFiltroEntidad('TODAS');
  };

  return (
    <>
      {/* KPIs de Auditoría */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Eventos Recuperados"
          valor={todosRegistros.length}
          acento="var(--accent-purple)"
          nota={`Límite actual: ${limite} eventos`}
        />
        <TarjetaKpi
          etiqueta="Modificaciones"
          valor={totalActualizaciones}
          acento="var(--accent-blue)"
          colorValor="var(--accent-blue)"
          nota="Actualizaciones de configuración"
        />
        <TarjetaKpi
          etiqueta="Creaciones"
          valor={totalCreaciones}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Nuevas categorías o usuarios"
        />
        <TarjetaKpi
          etiqueta="Eliminaciones"
          valor={totalEliminaciones}
          acento="var(--accent-red)"
          colorValor={totalEliminaciones > 0 ? 'var(--accent-red)' : undefined}
          nota="Bajas de registros del sistema"
        />
      </div>

      {/* Barra de Filtros */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Buscador */}
          <div style={{ position: 'relative', flex: '1 1 240px', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por usuario, email o cambios…"
              style={{
                width: '100%',
                padding: '8px 34px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 13,
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Limpiar búsqueda"
              >
                <IconoCerrar size={12} />
              </button>
            )}
          </div>

          {/* Filtro Acción */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Acción:
            </span>
            <select
              value={filtroAccion}
              onChange={(e) => setFiltroAccion(e.target.value as any)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODAS">Todas las acciones</option>
              <option value="CREAR">Creaciones (CREAR)</option>
              <option value="ACTUALIZAR">Modificaciones (ACTUALIZAR)</option>
              <option value="ELIMINAR">Eliminaciones (ELIMINAR)</option>
            </select>
          </div>

          {/* Filtro Entidad */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Módulo:
            </span>
            <select
              value={filtroEntidad}
              onChange={(e) => setFiltroEntidad(e.target.value)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODAS">Todos los módulos</option>
              <option value="Tenant">Empresa & Fiscal</option>
              <option value="Categoria">Líneas de Negocio</option>
              <option value="CategoriaGasto">Categorías de Costo</option>
              <option value="ParametrosPredictor">Motor de Precios IA</option>
              <option value="Usuario">Equipo & Usuarios</option>
            </select>
          </div>

          {/* Límite */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Cargar:
            </span>
            <select
              value={limite}
              onChange={(e) => setLimite(Number(e.target.value))}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value={25}>25 más recientes</option>
              <option value={50}>50 más recientes</option>
              <option value={100}>100 más recientes</option>
              <option value={200}>200 más recientes</option>
            </select>
          </div>

          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={12} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* Chips de filtros activos */}
        {hayFiltrosActivos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px dashed var(--border-subtle)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Filtros activos:</span>
            {busqueda.trim() && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-purple-soft)',
                  color: 'var(--accent-purple)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}
              >
                <span>Búsqueda: <strong>"{busqueda}"</strong></span>
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de búsqueda"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroAccion !== 'TODAS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background:
                    filtroAccion === 'CREAR'
                      ? 'var(--accent-green-soft)'
                      : filtroAccion === 'ACTUALIZAR'
                        ? 'var(--accent-blue-soft)'
                        : 'var(--accent-red-soft)',
                  color:
                    filtroAccion === 'CREAR'
                      ? 'var(--accent-green)'
                      : filtroAccion === 'ACTUALIZAR'
                        ? 'var(--accent-blue)'
                        : 'var(--accent-red)',
                  border: `1px solid ${
                    filtroAccion === 'CREAR'
                      ? 'rgba(16, 185, 129, 0.3)'
                      : filtroAccion === 'ACTUALIZAR'
                        ? 'rgba(59, 130, 246, 0.3)'
                        : 'rgba(239, 68, 68, 0.3)'
                  }`,
                }}
              >
                <span>Acción: <strong>{filtroAccion}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroAccion('TODAS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de acción"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            {filtroEntidad !== 'TODAS' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11.5,
                  background: 'var(--accent-yellow-soft)',
                  color: 'var(--accent-yellow)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                }}
              >
                <span>Módulo: <strong>{ETIQUETAS_ENTIDAD[filtroEntidad] ?? filtroEntidad}</strong></span>
                <button
                  type="button"
                  onClick={() => setFiltroEntidad('TODAS')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}
                  title="Quitar filtro de módulo"
                >
                  <IconoCerrar size={11} />
                </button>
              </span>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11, padding: '2px 8px', height: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconoRestablecer size={11} />
              <span>Restablecer todo</span>
            </button>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          Mostrando <strong>{registrosFiltrados.length}</strong> de {todosRegistros.length} eventos de auditoría
          {hayFiltrosActivos && ' (filtros aplicados)'}
        </div>
      </div>

      <Panel
        titulo="Historial Inmutable de Auditoría"
        subtitulo="Trazabilidad completa de modificaciones en parámetros corporativos, impuestos, categorías y accesos"
      >
        {registrosFiltrados.length === 0 ? (
          <Vacio
            titulo={hayFiltrosActivos ? 'Sin eventos que coincidan con la búsqueda' : 'Sin eventos de auditoría'}
            detalle={
              hayFiltrosActivos
                ? 'Prueba restableciendo los filtros o buscando con otro término.'
                : 'Los cambios de configuración se registrarán aquí automáticamente.'
            }
            accion={
              hayFiltrosActivos ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                  Restablecer filtros
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Fecha & Hora</th>
                  <th>Acción</th>
                  <th>Módulo / Entidad</th>
                  <th>Detalle de Cambios</th>
                  <th>Usuario Responsable</th>
                </tr>
              </thead>
              <tbody>
                {registrosFiltrados.map((item) => {
                  let detallesChips: { campo: string; valor: string }[] = [];
                  try {
                    if (item.cambios) {
                      const parsed = JSON.parse(item.cambios);
                      detallesChips = Object.entries(parsed).map(([k, v]) => ({
                        campo: k,
                        valor: typeof v === 'object' ? JSON.stringify(v) : String(v),
                      }));
                    }
                  } catch {
                    detallesChips = [{ campo: 'Detalle', valor: item.cambios ?? 'Sin detalle' }];
                  }

                  return (
                    <tr key={item.id}>
                      <td style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {formatearFechaHora(item.creadoEn)}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)',
                            background:
                              item.accion === 'CREAR'
                                ? 'rgba(16, 185, 129, 0.15)'
                                : item.accion === 'ELIMINAR'
                                ? 'rgba(239, 68, 68, 0.15)'
                                : 'rgba(59, 130, 246, 0.15)',
                            color:
                              item.accion === 'CREAR'
                                ? 'var(--accent-green)'
                                : item.accion === 'ELIMINAR'
                                ? 'var(--accent-red)'
                                : 'var(--accent-blue)',
                          }}
                        >
                          {item.accion}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {ETIQUETAS_ENTIDAD[item.entidad] ?? item.entidad}
                      </td>
                      <td style={{ maxWidth: 380 }}>
                        {detallesChips.length > 0 ? (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {detallesChips.map((chip, idx) => (
                              <span
                                key={idx}
                                style={{
                                  fontSize: 11,
                                  background: 'var(--bg-hover)',
                                  border: '1px solid var(--border-subtle)',
                                  borderRadius: 'var(--radius-sm)',
                                  padding: '1px 6px',
                                  color: 'var(--text-secondary)',
                                  wordBreak: 'break-word',
                                }}
                              >
                                <strong style={{ color: 'var(--text-primary)' }}>{chip.campo}:</strong> {chip.valor}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Sin detalle</span>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 12 }}>
                          {item.usuarioNombre || 'Sistema'}
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {item.usuarioEmail || ''}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
